import type { en } from "./en.ts";

export const fr: typeof en = {
    Navigation: {
        "review-cycle-detail-settings": {
            label: "Cycle d'évaluation",
            main: {
                label: "Cycle d'évaluation",
                progression: { label: "Progression" },
                scores: { label: "Scores" },
                calibration: { label: "Calibration" },
                questions: { label: "Résultats des questions" },
                themes: { label: "Thèmes" }
            }
        },
        "review-cycle-detail-home": {
            label: "Cycle d'évaluation",
            main: {
                label: "Cycle d'évaluation",
                progression: { label: "Progression" },
                scores: { label: "Scores" },
                calibration: { label: "Calibration" },
                questions: { label: "Résultats des questions" },
                themes: { label: "Thèmes" }
            }
        },
        "review-cycle-builder-settings": { label: "Modifier le cycle d'évaluation" },
        "review-cycle-builder-home": { label: "Modifier le cycle d'évaluation" },
        "review-cycle-new-settings": { label: "Nouveau cycle d'évaluation" },
        "review-cycle-new-home": { label: "Nouveau cycle d'évaluation" },
        "job-architecture-ai-setup": {
            label: "Configurez votre architecture de postes"
        },
        "admin-settings-job": { label: "Poste" },
        "admin-settings": {
            label: "Admin",
            organization: {
                label: "Organisation",
                company: {
                    label: "Entreprise",
                    profile: { label: "Profil" },
                    "mission-and-values": { label: "Mission et valeurs" }
                },
                people: {
                    label: "Personnes",
                    "manage-people": { label: "Gérer les personnes" },
                    "people-fields": { label: "Champs des employés" },
                    jobs: { label: "Postes" },
                    "jobs-get-started": { label: "Configurez votre architecture de postes" },
                    "job-architecture": { label: "Architecture des postes" },
                    "jobs-architecture-ai": { label: "Configurer avec l'IA" },
                    "job-new": { label: "Nouveau poste" },
                    segments: {
                        label: "Segments",
                        mine: { label: "Mes segments" },
                        "shared-with-me": { label: "Partagés avec moi" }
                    },
                    "segment-new": { label: "Nouveau segment" },
                    "segment-edit": { label: "Segment" },
                    "manage-people-detail": {
                        label: "Personne",
                        details: { label: "Détails" },
                        job: { label: "Poste" },
                        permissions: { label: "Permissions" }
                    }
                },
                "roles-and-permissions": { label: "Rôles et permissions" },
                integrations: { label: "Intégrations" },
                billing: { label: "Facturation" },
                features: { label: "Fonctionnalités" }
            },
            agent: {
                label: "Agent",
                general: { label: "Général" },
                "skill-library": { label: "Compétences" },
                "skill-detail": { label: "Compétence" },
                "connected-apps": {
                    label: "Applications connectées",
                    "knowledge-sources": { label: "Sources de connaissances" },
                    access: { label: "Accès" }
                }
            },
            features: {
                label: "Fonctionnalités",
                engagement: {
                    label: "Engagement",
                    settings: { label: "Paramètres" },
                    access: { label: "Accès" }
                },
                goals: { label: "Objectifs" },
                "good-vibes": {
                    label: "Bonnes Vibes",
                    settings: { label: "Paramètres" },
                    access: { label: "Accès" }
                },
                meetings: { label: "Réunions" },
                "performance-reviews": {
                    label: "Évaluations de performance",
                    "review-cycles": { label: "Cycles d'évaluation" },
                    access: { label: "Accès" }
                },
                plans: { label: "Plans" }
            }
        }
    },
    CompanyPage: {
        loading: "Chargement des informations de l'entreprise",
        loadError: "Nous n'avons pas pu charger les informations de votre entreprise.",
        retry: "Réessayer",
        savedConfirmation: "Modifications enregistrées",
        saveErrorToast: "Nous n'avons pas pu enregistrer vos modifications. Veuillez réessayer.",
        discard: "Annuler",
        saveChanges: "Enregistrer les modifications",
        logo: {
            label: "Logo",
            description: "PNG, JPG ou GIF. 5 Mo maximum.",
            alt: "Logo de l'entreprise",
            uploadButton: "Téléverser un logo",
            uploadedToast: "Logo mis à jour",
            error: {
                INVALID_IMAGE_FORMAT: "Ce fichier n'est pas une image prise en charge. Utilisez un PNG, JPG ou GIF.",
                IMAGE_TOO_LARGE: "Cette image est trop grande. Utilisez un fichier de moins de 5 Mo.",
                generic: "Nous n'avons pas pu téléverser votre logo. Veuillez réessayer."
            }
        },
        name: {
            label: "Nom de l'entreprise",
            description: "Le nom que votre équipe voit dans Workleap.",
            placeholder: "Le nom de votre entreprise",
            required: "Le nom de l'entreprise ne peut pas être vide."
        },
        timeZone: {
            label: "Fuseau horaire",
            description: "Les employés peuvent le remplacer dans leurs préférences.",
            placeholder: "Rechercher un fuseau horaire…"
        },
        language: {
            label: "Langue par défaut",
            description: "Les employés peuvent la remplacer dans leurs préférences.",
            placeholder: "Sélectionner une langue",
            options: {
                "en-US": "Anglais (É.-U.)",
                "fr-CA": "Français (Canada)"
            }
        },
        mission: {
            label: "Mission",
            description: "Un bref énoncé de ce que votre entreprise cherche à accomplir.",
            placeholder: "Décrivez la mission de votre entreprise…"
        },
        values: {
            label: "Valeurs de l'entreprise",
            description: "Les principes selon lesquels votre équipe travaille. Glissez pour réordonner.",
            add: "Ajouter une valeur",
            empty: "Aucune valeur d'entreprise pour l'instant.",
            nameLabel: "Nom de la valeur",
            namePlaceholder: "Nom de la valeur",
            descriptionLabel: "Description",
            descriptionPlaceholder: "Décrivez cette valeur…",
            drag: "Réordonner la valeur",
            remove: "Supprimer la valeur",
            nameRequired: "Le nom de la valeur ne peut pas être vide.",
            reordered: "{{name}}, position {{position}} sur {{total}}"
        }
    },
    PeoplePage: {
        description: "Les personnes et les membres peuvent être gérés dans Workleap Classic."
    },
    ManagePeopleList: {
        totalCount_one: "{{count}} personne",
        totalCount_other: "{{count}} personnes",
        noResults: "Aucune personne ne correspond à votre recherche",
        bulkDeactivatedToast: "Personnes désactivées",
        bulkDeactivatedSelfSkippedToast: "Personnes désactivées. Vous ne pouvez pas vous désactiver vous-même, votre compte a donc été ignoré.",
        bulkDeactivateErrorToast: "Impossible de désactiver ces personnes. Veuillez réessayer.",
        bulkReactivatedToast: "Personnes réactivées",
        bulkReactivateErrorToast: "Impossible de réactiver ces personnes. Veuillez réessayer.",
        bulkInvitedToast_one: "{{count}} personne invitée.",
        bulkInvitedToast_other: "{{count}} personnes invitées.",
        bulkInvitedPartialToast_one: "{{invited}} personne invitée sur {{total}}.",
        bulkInvitedPartialToast_other: "{{invited}} personnes invitées sur {{total}}.",
        bulkSendInviteErrorToast: "Une erreur s'est produite. Veuillez réessayer.",
        bulkNobodyInvitedToast: "Personne n'a pu être invité pour le moment.",
        bulkInviteResentToast_one: "{{count}} invitation renvoyée.",
        bulkInviteResentToast_other: "{{count}} invitations renvoyées.",
        bulkInviteResentPartialToast_one: "{{resent}} invitation renvoyée sur {{total}}.",
        bulkInviteResentPartialToast_other: "{{resent}} invitations renvoyées sur {{total}}.",
        bulkResendInviteErrorToast: "Une erreur s'est produite. Veuillez réessayer.",
        bulkNobodyResentToast: "Aucune invitation envoyée. Elles ont peut-être été invitées au cours des dernières 24 heures.",
        bulkRemovedToast_one: "{{count}} personne supprimée.",
        bulkRemovedToast_other: "{{count}} personnes supprimées.",
        bulkRemovedSelfSkippedToast_one: "{{count}} personne supprimée. Vous ne pouvez pas vous supprimer vous-même, votre compte a donc été ignoré.",
        bulkRemovedSelfSkippedToast_other: "{{count}} personnes supprimées. Vous ne pouvez pas vous supprimer vous-même, votre compte a donc été ignoré.",
        bulkRemoveErrorToast: "Une erreur s'est produite. Veuillez réessayer.",
        selectAllMatching: "Sélectionner les {{count}} personnes",
        empty: {
            title: "Aucune personne pour l'instant",
            description: "Invitez des personnes dans votre organisation pour les voir ici."
        },
        error: {
            title: "Impossible de charger les personnes.",
            description: "Veuillez réessayer plus tard.",
            retry: "Réessayer",
            refreshBanner: "Impossible d'actualiser — affichage des derniers résultats."
        },
        liveRegion: {
            loading: "Chargement des personnes",
            loadingMoreAnnouncement: "Chargement d'autres personnes",
            loadedMore_one: "{{count}} personne supplémentaire chargée",
            loadedMore_other: "{{count}} personnes supplémentaires chargées",
            results_one: "{{count}} personne",
            results_other: "{{count}} personnes",
            sortedAscending: "Tableau trié par {{column}}, ordre croissant",
            sortedDescending: "Tableau trié par {{column}}, ordre décroissant"
        }
    },
    ManagePeopleToolbar: {
        searchPlaceholder: "Rechercher par nom, courriel",
        searchAriaLabel: "Rechercher des personnes",
        statusFilter: {
            label: "Statut",
            ariaLabel: "Filtrer par statut",
            clearFilter: "Effacer le filtre"
        },
        rolesFilter: {
            label: "Rôles",
            ariaLabel: "Filtrer par rôles",
            clearFilter: "Effacer le filtre"
        },
        departmentFilter: {
            label: "Département",
            ariaLabel: "Filtrer par département",
            clearFilter: "Effacer le filtre"
        },
        managerFilter: {
            label: "Gestionnaire",
            ariaLabel: "Filtrer par gestionnaire",
            searchPlaceholder: "Rechercher par nom",
            loading: "Chargement...",
            noResults: "Aucun résultat",
            loadMore: "Charger plus",
            clearFilter: "Effacer le filtre",
            liveRegion: {
                loadedMore_one: "{{count}} personne de plus chargée",
                loadedMore_other: "{{count}} personnes de plus chargées"
            }
        },
        jobFilter: {
            label: "Poste",
            ariaLabel: "Filtrer par poste",
            searchPlaceholder: "Rechercher par titre",
            loading: "Chargement...",
            noResults: "Aucun résultat",
            loadMore: "Charger plus",
            clearFilter: "Effacer le filtre",
            liveRegion: {
                loadedMore_one: "{{count}} poste de plus chargé",
                loadedMore_other: "{{count}} postes de plus chargés"
            }
        },
        addFilter: "Ajouter un filtre",
        removeFilter: "Retirer le filtre {{filter}}",
        resetFilters: "Effacer les filtres"
    },
    ManagePeopleTable: {
        ariaLabel: "Personnes",
        columns: {
            name: "Nom",
            roles: "Rôles",
            hireDate: "Date d'embauche",
            status: "Statut",
            lastSession: "Dernière session",
            actions: "Actions"
        },
        lastSession: {
            never: "Jamais",
            today: "Aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours",
            weeks_one: "il y a {{count}} semaine",
            weeks_other: "il y a {{count}} semaines"
        }
    },
    managePeople: {
        clearSelection: "Effacer la sélection",
        statuses: {
            active: "Actif",
            created: "Créé",
            invited: "Invitation envoyée",
            deactivated: "Désactivé",
            nonSeat: "Sans siège"
        },
        roles: {
            admin: "Administrateur",
            executive: "Cadre",
            collaborator: "Collaborateur",
            manager: "Gestionnaire",
            employee: "Employé"
        },
        permissions: {
            learnMore: "En savoir plus sur les permissions",
            learnMoreUrl: "https://help.workleap.com/fr/articles/11010624-gerer-les-roles-et-les-autorisations-des-utilisateurs"
        },
        unsavedChanges: {
            title: "Quitter sans enregistrer?",
            body: "Vos modifications seront perdues si vous quittez cette page.",
            stayButton: "Continuer la modification",
            leaveButton: "Abandonner les modifications"
        },
        toasts: {
            deactivatedToast: "Personne désactivée",
            deactivateErrorToast: "Impossible de désactiver cette personne. Veuillez réessayer.",
            reactivatedToast: "Personne réactivée",
            reactivateErrorToast: "Impossible de réactiver cette personne. Veuillez réessayer.",
            inviteSentToast: "Invitation envoyée",
            sendInviteErrorToast: "Une erreur s'est produite. Veuillez réessayer.",
            inviteResentToast: "Invitation renvoyée",
            inviteRecentlySentToast: "Invitation déjà envoyée récemment",
            noInvitableProductToast: "Cette personne n'est associée à aucun produit invitable",
            resendInviteErrorToast: "Une erreur s'est produite. Veuillez réessayer.",
            removedToast: "Personne supprimée",
            removeErrorToast: "Une erreur s'est produite. Veuillez réessayer."
        }
    },
    PermissionOptionList: {
        helperLine: "Tout le monde obtient l'accès employé standard. Ajoutez des permissions pour en accorder plus.",
        roles: {
            admin: { title: "Administrateur", description: "Gère les paramètres de l'organisation, la facturation, les intégrations et les permissions." },
            executive: { title: "Cadre", description: "Gère le répertoire des personnes et la structure des équipes. Voit les analyses et les données de chaque employé." },
            collaborator: { title: "Collaborateur", description: "Consulte les paramètres et les rapports. Ne peut pas modifier les membres." },
            manager: { title: "Gestionnaire", description: "Voit les données des personnes de sa ligne hiérarchique. Accordé automatiquement dès qu'une personne relève de lui." }
        },
        automaticBadge: "Automatique",
        selfReadOnlyHint: "Vous ne pouvez pas modifier vos propres permissions.",
        adminOnlyHint: "Seuls les administrateurs peuvent modifier ceci.",
        mixedHint: "Certaines des personnes sélectionnées ont ce rôle. Si vous n'y touchez pas, chacune conserve son accès actuel."
    },
    ManagePermissionsDialog: {
        title: "Gérer les permissions de {{name}}",
        bulkTitle_one: "Gérer les permissions de {{count}} personne",
        bulkTitle_other: "Gérer les permissions de {{count}} personnes",
        bulkBanner_one: "Ces permissions s'appliquent à la {{count}} personne sélectionnée. Ce qui reste inchangé conserve son accès actuel.",
        bulkBanner_other: "Ces permissions s'appliquent aux {{count}} personnes sélectionnées. Ce qui reste inchangé conserve l'accès actuel de chaque personne.",
        cancel: "Annuler",
        submit: "Mettre à jour les permissions",
        submitBulk_one: "Mettre à jour {{count}} personne",
        submitBulk_other: "Mettre à jour {{count}} personnes",
        successToast: "Permissions mises à jour",
        successBulkToast_one: "Permissions mises à jour pour {{count}} personne",
        successBulkToast_other: "Permissions mises à jour pour {{count}} personnes",
        updateErrorToast: "Impossible de mettre à jour les permissions. Veuillez réessayer.",
        selfAdminLockedToast: "Vous ne pouvez pas modifier vos propres permissions.",
        liveRegion: {
            loading: "Chargement des permissions actuelles de la sélection"
        }
    },
    AddPeopleMenu: {
        label: "Ajouter des personnes",
        inviteByEmail: "Inviter par courriel",
        inviteByText: "Inviter par texto",
        addWithoutSeat: "Ajouter sans siège",
        importFromFile: "Importer depuis un fichier"
    },
    ManagePeopleHeaderActions: {
        manageSync: "Gérer la synchronisation",
        syncedFromAt: "Synchronisé depuis {{integration}} {{timeAgo}}.",
        syncedFrom: "Synchronisé depuis {{integration}}.",
        syncedAt: "Synchronisé {{timeAgo}}."
    },
    AddWithoutSeatDialog: {
        title: "Ajouter sans siège",
        intro: "Ces personnes apparaissent dans le répertoire et l'organigramme. Elles ne peuvent pas se connecter et n'utilisent aucun siège.",
        names: {
            label: "Noms complets (séparez-les par des virgules)",
            placeholder: "Alex Kim, Riley Chen",
            tooMany: "Ajoutez au maximum {{count}} personnes à la fois."
        },
        manager: {
            label: "Gestionnaire",
            placeholder: "Sélectionner un gestionnaire",
            description: "Détermine à qui ces personnes se rapportent. Vous pourrez le changer plus tard dans leur profil.",
            noResults: "Aucun résultat",
            searchError: "Impossible de charger les gestionnaires. Veuillez réessayer.",
            loading: "Recherche de gestionnaires",
            loadingMore: "Chargement d'autres gestionnaires",
            liveRegion: {
                loadedMore_one: "{{count}} gestionnaire de plus chargé",
                loadedMore_other: "{{count}} gestionnaires de plus chargés"
            }
        },
        cancel: "Annuler",
        submit: "Ajouter des personnes",
        successToast_one: "Personne ajoutée",
        successToast_other: "{{count}} personnes ajoutées",
        addErrorToast: "Impossible d'ajouter ces personnes. Veuillez réessayer."
    },
    InviteByEmailDialog: {
        title: "Inviter par courriel",
        stepIndicator: "Étape {{current}} sur {{total}}",
        emails: {
            label: "Adresses courriel (séparez-les par des virgules)",
            placeholder: "alex@entreprise.com, riley@entreprise.com",
            invalid_one: "{{emails}} n'est pas une adresse courriel valide.",
            invalid_other: "Ces adresses courriel ne sont pas valides : {{emails}}",
            duplicate_one: "{{emails}} est entré plus d'une fois.",
            duplicate_other: "Ces adresses sont entrées plus d'une fois : {{emails}}",
            tooMany: "Invitez au maximum {{count}} personnes à la fois.",
            alreadyMember_one: "{{count}} adresse courriel appartient déjà à une personne de votre organisation : {{emails}}",
            alreadyMember_other: "{{count}} adresses courriel appartiennent déjà à des personnes de votre organisation : {{emails}}",
            unknown_one: "Impossible d'ajouter {{count}} adresse courriel : {{emails}}",
            unknown_other: "Impossible d'ajouter {{count}} adresses courriel : {{emails}}",
            checkFailed: "Impossible de vérifier ces adresses courriel. Veuillez réessayer."
        },
        manager: {
            label: "Gestionnaire",
            placeholder: "Sélectionner un gestionnaire",
            description: "Détermine à qui ces personnes se rapportent. Vous pourrez le changer plus tard dans leur profil.",
            noResults: "Aucun résultat",
            searchError: "Impossible de charger les gestionnaires. Veuillez réessayer.",
            loading: "Recherche de gestionnaires",
            loadingMore: "Chargement d'autres gestionnaires",
            liveRegion: {
                loadedMore_one: "{{count}} gestionnaire de plus chargé",
                loadedMore_other: "{{count}} gestionnaires de plus chargés"
            }
        },
        permissionsLabel: "Permissions",
        cancel: "Annuler",
        continue: "Continuer",
        back: "Retour",
        sendInvite_one: "Envoyer l'invitation",
        sendInvite_other: "Envoyer {{count}} invitations",
        moreOptions: "Plus d'options d'invitation",
        createWithoutInviting: "Créer sans inviter",
        successToast: "Invitation envoyée",
        successBulkToast_other: "Invitations envoyées à {{count}} personnes",
        sendErrorToast: "Impossible d'envoyer l'invitation. Veuillez réessayer.",
        createSuccessToast: "Personne créée",
        createSuccessBulkToast_other: "{{count}} personnes créées",
        createErrorToast: "Impossible de créer ces personnes. Veuillez réessayer."
    },
    InviteByTextDialog: {
        title: "Inviter par texto",
        stepIndicator: "Étape {{current}} sur {{total}}",
        phoneNumbers: {
            label: "Numéros de téléphone (séparez-les par des virgules)",
            placeholder: "+1 555 010 1234, +1 555 010 5678",
            invalid_one: "{{phoneNumbers}} n'est pas un numéro de téléphone valide. Incluez l'indicatif de pays.",
            invalid_other: "Ces numéros de téléphone ne sont pas valides : {{phoneNumbers}}. Incluez l'indicatif de pays.",
            duplicate_one: "{{phoneNumbers}} est entré plus d'une fois.",
            duplicate_other: "Ces numéros sont entrés plus d'une fois : {{phoneNumbers}}",
            tooMany: "Invitez au maximum {{count}} personnes à la fois.",
            alreadyMember_one: "{{count}} numéro de téléphone appartient déjà à une personne de votre organisation : {{phoneNumbers}}",
            alreadyMember_other: "{{count}} numéros de téléphone appartiennent déjà à des personnes de votre organisation : {{phoneNumbers}}",
            unknown_one: "Impossible d'ajouter {{count}} numéro de téléphone : {{phoneNumbers}}",
            unknown_other: "Impossible d'ajouter {{count}} numéros de téléphone : {{phoneNumbers}}",
            checkFailed: "Impossible de vérifier ces numéros de téléphone. Veuillez réessayer."
        },
        manager: {
            label: "Gestionnaire",
            placeholder: "Sélectionner un gestionnaire",
            description: "Détermine à qui ces personnes se rapportent. Vous pourrez le changer plus tard dans leur profil.",
            noResults: "Aucun résultat",
            searchError: "Impossible de charger les gestionnaires. Veuillez réessayer.",
            loading: "Recherche de gestionnaires",
            loadingMore: "Chargement d'autres gestionnaires",
            liveRegion: {
                loadedMore_one: "{{count}} gestionnaire de plus chargé",
                loadedMore_other: "{{count}} gestionnaires de plus chargés"
            }
        },
        permissionsLabel: "Permissions",
        cancel: "Annuler",
        continue: "Continuer",
        back: "Retour",
        sendInvite_one: "Envoyer l'invitation",
        sendInvite_other: "Envoyer {{count}} invitations",
        moreOptions: "Plus d'options d'invitation",
        createWithoutInviting: "Créer sans inviter",
        successToast: "Invitation envoyée",
        successBulkToast_other: "Invitations envoyées à {{count}} personnes",
        sendErrorToast: "Impossible d'envoyer l'invitation. Veuillez réessayer.",
        createSuccessToast: "Personne créée",
        createSuccessBulkToast_other: "{{count}} personnes créées",
        createErrorToast: "Impossible de créer ces personnes. Veuillez réessayer."
    },
    ManagePeopleRowMenu: {
        ariaLabel: "Actions pour {{name}}",
        edit: "Modifier l'utilisateur",
        managePermissions: "Gérer les permissions",
        sendInvite: "Envoyer l'invitation",
        resendInvite: "Renvoyer l'invitation",
        reactivate: "Réactiver",
        deactivate: "Désactiver",
        remove: "Supprimer l'utilisateur"
    },
    RemovePersonDialog: {
        title: "Supprimer cette personne?",
        body: "Cette action est permanente et ne doit être appliquée qu'à une personne ayant quitté votre organisation. Une fois supprimé, son compte ne peut pas être restauré, et toutes les données associées seront supprimées de tous les rapports et de toutes les vues dans Workleap. Les changements prendront effet dans quelques minutes.",
        confirmationLabel: "Je comprends",
        confirm: "Supprimer",
        cancel: "Annuler"
    },
    DeactivatePersonDialog: {
        title: "Désactiver cette personne?",
        body: "Cette action est réversible et peut être appliquée à une personne en congé prolongé. Vous pouvez restaurer un compte désactivé pour accéder à l'ensemble des informations et des données qu'il comprenait.",
        confirm: "Désactiver",
        cancel: "Annuler"
    },
    ManagePeopleSelectionBar: {
        selectedCount_one: "{{count}} sélectionnée",
        selectedCount_other: "{{count}} sélectionnées",
        clear: "Effacer",
        managePermissions: "Gérer les permissions",
        managePermissionsWithCount: "Gérer les permissions ({{count}})",
        more: "Plus",
        reactivate: "Réactiver",
        reactivateWithCount: "Réactiver ({{count}})",
        sendInvite: "Envoyer l'invitation",
        sendInviteWithCount: "Envoyer l'invitation ({{count}})",
        resendInvite: "Renvoyer l'invitation",
        resendInviteWithCount: "Renvoyer l'invitation ({{count}})",
        deactivate: "Désactiver",
        deactivateWithCount: "Désactiver ({{count}})",
        remove: "Supprimer",
        removeWithCount: "Supprimer ({{count}})"
    },
    DeactivatePeopleDialog: {
        title_one: "Désactiver cette personne?",
        title_other: "Désactiver {{count}} personnes?",
        body: "Cette action est réversible et peut être appliquée aux personnes en congé prolongé. Vous pouvez restaurer un compte désactivé pour accéder à l'ensemble des informations et des données qu'il comprenait.",
        confirm: "Désactiver",
        cancel: "Annuler"
    },
    RemovePeopleDialog: {
        title_one: "Supprimer cette personne?",
        title_other: "Supprimer {{count}} personnes?",
        body: "Cette action est permanente et ne doit être appliquée qu'aux personnes ayant quitté votre organisation. Une fois supprimé, leur compte ne peut pas être restauré, et toutes les données associées seront supprimées de tous les rapports et de toutes les vues dans Workleap. Les changements prendront effet dans quelques minutes.",
        confirmationLabel: "Je comprends",
        confirm: "Supprimer",
        cancel: "Annuler"
    },
    PersonDetailTabsHeading: {
        liveRegion: {
            loading: "Chargement de la personne"
        },
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            managePeople: "Gérer les personnes"
        }
    },
    PersonDetailPermissionsPage: {
        heading: "Permissions",
        subtitle: "Détermine ce que cette personne peut voir et faire dans Workleap. Une personne peut en détenir plusieurs.",
        savedConfirmation: "Modifications enregistrées",
        saveErrorToast: "Enregistrement impossible. Veuillez réessayer.",
        selfAdminLockedError: "Vous ne pouvez pas modifier vos propres permissions.",
        discard: "Annuler",
        saveChanges: "Enregistrer les modifications",
        liveRegion: {
            loading: "Chargement des permissions"
        }
    },
    PersonDetailJobPage: {
        heading: "Informations d'emploi",
        department: {
            label: "Département",
            placeholder: "Sélectionner un département"
        },
        division: {
            label: "Division",
            placeholder: "Sélectionner une division"
        },
        manager: {
            label: "Gestionnaire",
            placeholder: "Rechercher une personne",
            noResults: "Aucune personne ne correspond à votre recherche",
            selfError: "Une personne ne peut pas être son propre gestionnaire.",
            searchError: "Impossible de charger les personnes. Veuillez réessayer.",
            loading: "Recherche de personnes",
            loadingMore: "Chargement d'autres personnes",
            liveRegion: {
                loadedMore_one: "{{count}} personne de plus chargée",
                loadedMore_other: "{{count}} personnes de plus chargées"
            }
        },
        hireDate: {
            label: "Date d'embauche"
        },
        termination: {
            label: "Fin d'emploi",
            dateLabel: "Date de fin d'emploi",
            reasonLabel: "Motif de fin d'emploi",
            reasonPlaceholder: "Motif (volontaire, rendement, restructuration, etc.)",
            beforeHireError: "La date de fin d'emploi ne peut pas précéder la date d'embauche."
        },
        lockedIconLabel: "Pourquoi ce champ est-il verrouillé ?",
        lockedTooltip: "Cette valeur est synchronisée avec {{integrationName}} et ne peut pas être modifiée manuellement.",
        lockedTooltipGeneric: "Cette valeur est synchronisée automatiquement et ne peut pas être modifiée manuellement.",
        savedConfirmation: "Modifications enregistrées",
        saveErrorToast: "Enregistrement impossible. Veuillez réessayer.",
        discard: "Annuler",
        saveChanges: "Enregistrer les modifications",
        liveRegion: {
            loading: "Chargement des informations d'emploi"
        }
    },
    PersonDetailJobHistoryCard: {
        heading: "Historique",
        addButton: "Ajouter un emploi",
        ariaLabel: "Historique d'emploi",
        columns: {
            effectiveDate: "Date d'entrée en vigueur",
            jobTitle: "Titre du poste",
            employmentType: "Type d'emploi",
            hoursPerWeek: "Heures par semaine",
            actions: "Actions"
        },
        currentBadge: "Actuel",
        editRow: "Modifier {{jobTitle}}",
        deleteRow: "Supprimer {{jobTitle}}",
        untitledJob: "Poste sans titre",
        lockedTooltip: "Ces enregistrements sont synchronisés avec {{integrationName}} et ne peuvent pas être modifiés manuellement.",
        lockedTooltipGeneric: "Ces enregistrements sont synchronisés automatiquement et ne peuvent pas être modifiés manuellement.",
        empty: {
            title: "Aucun historique d'emploi pour l'instant",
            description: "Suivez les postes, le type d'emploi, les heures et les dates d'entrée en vigueur de {{firstName}}.",
            descriptionFallback: "Suivez les postes, le type d'emploi, les heures et les dates d'entrée en vigueur de cette personne.",
            addButton: "Ajouter le premier emploi"
        },
        loadError: {
            title: "Impossible de charger l'historique d'emploi.",
            retry: "Réessayer"
        },
        liveRegion: {
            loading: "Chargement de l'historique d'emploi"
        },
        addedToast: "Emploi ajouté",
        updatedToast: "Emploi mis à jour",
        deletedToast: "Emploi supprimé",
        delete: {
            title: "Supprimer cet emploi?",
            description: "{{jobTitle}} • {{effectiveDate}} — cela le retire de l'historique de {{firstName}}. Cette action est irréversible.",
            descriptionFallback: "{{jobTitle}} • {{effectiveDate}} — cela le retire de l'historique de cette personne. Cette action est irréversible.",
            confirm: "Supprimer",
            error: "Suppression impossible. Veuillez réessayer."
        },
        jobTitle: {
            label: "Titre du poste",
            placeholder: "p. ex. Directrice marketing",
            searching: "Recherche de titres de poste en cours…",
            loadingMore: "Chargement d'autres titres de poste",
            createOption: "Créer « {{title}} »",
            pendingEditHint: "Choisissez une suggestion ou cliquez sur « Créer » pour utiliser ce titre.",
            liveRegion: {
                loadedMore_one: "{{count}} titre de poste de plus chargé",
                loadedMore_other: "{{count}} titres de poste de plus chargés"
            }
        },
        effectiveDate: {
            label: "Date d'entrée en vigueur",
            backdateNote: "Cet emploi sera ajouté à l'historique. L'emploi actuel ne sera pas modifié."
        },
        employmentType: {
            label: "Type d'emploi",
            placeholder: "Sélectionner...",
            options: {
                FullTime: "Temps plein",
                PartTime: "Temps partiel",
                Intern: "Stage",
                Contractor: "Contractuel",
                Freelance: "Pigiste",
                Other: "Autre"
            }
        },
        hoursPerWeek: {
            label: "Heures par semaine"
        },
        flsaStatus: {
            label: "Statut FLSA",
            placeholder: "Sélectionner...",
            options: {
                Exempt: "Exempté",
                SalariedNonexempt: "Salarié non exempté",
                Nonexempt: "Non exempté",
                Owner: "Propriétaire",
                Other: "Autre"
            }
        },
        add: {
            title: "Ajouter un nouvel emploi",
            subtitle: "Cet emploi deviendra l'emploi actif de {{firstName}} à la date d'entrée en vigueur. L'emploi précédent sera versé à l'historique.",
            subtitleFallback: "Cet emploi deviendra l'emploi actif de la personne à la date d'entrée en vigueur. L'emploi précédent sera versé à l'historique.",
            submit: "Ajouter l'emploi"
        },
        edit: {
            title: "Modifier l'emploi",
            subtitle: "Modification du {{effectiveDate}}.",
            subtitleUndated: "Modification de cette entrée d'emploi.",
            submit: "Enregistrer les modifications"
        },
        cancel: "Annuler",
        error: {
            required: "Ce champ est obligatoire.",
            hoursPositive: "Saisissez un nombre supérieur à 0.",
            save: "Enregistrement impossible. Veuillez réessayer."
        }
    },
    PersonDetailsPage: {
        liveRegion: {
            loading: "Chargement des détails"
        }
    },
    PersonDetailsForm: {
        savedConfirmation: "Modifications enregistrées",
        discard: "Annuler",
        saveChanges: "Enregistrer les modifications",
        error: {
            required: "Ce champ est obligatoire.",
            futureBirthday: "La date de naissance ne peut pas être dans le futur.",
            emailRequired: "Le courriel professionnel est obligatoire.",
            invalidEmail: "Saisissez une adresse courriel valide.",
            emailUnavailable: "Cette adresse courriel n'est pas disponible.",
            emailAlreadyMember: "Cette adresse courriel est déjà utilisée par une personne de votre organisation.",
            emailMaxOrganizations: "Cette adresse courriel a atteint le nombre maximal d'espaces de travail Workleap.",
            VALIDATION_FAILED: "Certains renseignements n'ont pas pu être enregistrés. Vérifiez les champs signalés et réessayez.",
            NOT_FOUND: "Cette personne n'existe plus.",
            generic: "Enregistrement impossible. Veuillez réessayer.",
            communicationLanguageSaveFailed: "Impossible d'enregistrer la langue de communication. Veuillez réessayer.",
            customFieldsSaveFailed: "Impossible d'enregistrer les champs personnalisés. Veuillez réessayer."
        }
    },
    PersonPhotoField: {
        label: "Photo de profil",
        changeButton: "Changer la photo",
        removeButton: "Supprimer la photo",
        cancel: "Annuler",
        lockedIconLabel: "Pourquoi ce champ est-il verrouillé ?",
        lockedTooltip: "Cette valeur est synchronisée avec {{integrationName}} et ne peut pas être modifiée manuellement.",
        lockedTooltipGeneric: "Cette valeur est synchronisée automatiquement et ne peut pas être modifiée manuellement.",
        uploadedToast: "Photo de profil mise à jour",
        removedToast: "Photo supprimée.",
        crop: {
            title: "Recadrer la photo",
            instructions: "Faites glisser la photo ou utilisez les touches fléchées pour la repositionner, et le curseur de zoom pour la redimensionner.",
            areaLabel: "Zone de recadrage de la photo",
            zoomLabel: "Zoom",
            confirm: "Enregistrer la photo"
        },
        remove: {
            title: "Supprimer la photo de profil ?",
            description: "{{name}} affichera l'avatar par défaut. Cette action est irréversible.",
            confirm: "Supprimer"
        },
        error: {
            INVALID_IMAGE_FORMAT: "Choisissez une image PNG, JPEG ou GIF.",
            IMAGE_TOO_LARGE: "Cette image est trop volumineuse. Choisissez-en une de moins de 5 Mo.",
            generic: "Téléversement de la photo impossible. Veuillez réessayer."
        }
    },
    PersonIdentityCard: {
        heading: "Identité",
        firstName: "Prénom",
        lastName: "Nom",
        preferredName: "Nom d'usage",
        preferredNameHint: "Remplace le prénom partout où il apparaît",
        employeeId: "Numéro d'employé",
        employeeIdPlaceholder: "EMP-00000",
        birthday: "Date de naissance",
        gender: "Genre",
        pronouns: "Pronoms",
        selectPlaceholder: "Sélectionnez une option",
        lockedIconLabel: "Pourquoi ce champ est-il verrouillé ?",
        lockedTooltip: "Cette valeur est synchronisée avec {{integrationName}} et ne peut pas être modifiée manuellement.",
        lockedTooltipGeneric: "Cette valeur est synchronisée automatiquement et ne peut pas être modifiée manuellement."
    },
    PersonContactLocationCard: {
        heading: "Coordonnées et emplacement",
        email: "Courriel professionnel",
        emailUsedElsewhere: "Ce courriel est utilisé dans un autre espace de travail et ne peut pas être modifié.",
        emailChecking: "Vérification de la disponibilité du courriel",
        officePhone: "Téléphone au bureau",
        officePhonePlaceholder: "+1 555 555 5555",
        workLocation: "Lieu de travail",
        timeZone: "Fuseau horaire",
        timeZonePlaceholder: "Rechercher un fuseau horaire",
        city: "Ville",
        region: "Région",
        country: "Pays",
        selectPlaceholder: "Sélectionnez une option",
        lockedIconLabel: "Pourquoi ce champ est-il verrouillé ?",
        lockedTooltip: "Cette valeur est synchronisée avec {{integrationName}} et ne peut pas être modifiée manuellement.",
        lockedTooltipGeneric: "Cette valeur est synchronisée automatiquement et ne peut pas être modifiée manuellement."
    },
    PersonPreferencesCard: {
        heading: "Préférences",
        language: {
            label: "Langue de l'application",
            placeholder: "Sélectionnez une langue",
            helperText: "Certaines fonctionnalités de Workleap Classic ne sont disponibles qu'en anglais.",
            options: {
                "en-US": "Anglais (É.-U.)",
                "fr-CA": "Français (Canada)"
            }
        },
        communicationLanguage: {
            label: "Langue de communication",
            placeholder: "Sélectionnez une langue",
            helperText: "Langue utilisée pour les notifications et les sondages Workleap."
        }
    },
    PersonAboutCard: {
        heading: "À propos",
        bio: "À mon sujet",
        bioPlaceholder: "Une courte présentation",
        interests: "Intérêts",
        competencies: "Compétences",
        tagPlaceholder: "Ajouter un élément",
        duplicateTag: "Cet élément a déjà été ajouté.",
        removeTag: "Retirer {{tag}}",
        lockedIconLabel: "Pourquoi ce champ est-il verrouillé ?",
        lockedTooltip: "Cette valeur est synchronisée avec {{integrationName}} et ne peut pas être modifiée manuellement.",
        lockedTooltipGeneric: "Cette valeur est synchronisée automatiquement et ne peut pas être modifiée manuellement."
    },
    PersonCustomFieldsCard: {
        heading: "Champs personnalisés",
        subtitle: "Champs que votre organisation a ajoutés en plus de ceux de Workleap.",
        error: {
            title: "Nous n'avons pas pu charger les champs personnalisés.",
            description: "Les autres renseignements de cette personne restent modifiables.",
            retry: "Réessayer"
        }
    },
    PersonCustomField: {
        selectPlaceholder: "Sélectionnez une option",
        restrictedBadge: "Administrateurs et dirigeants seulement",
        lockedIconLabel: "Pourquoi ce champ est-il verrouillé ?",
        lockedTooltip: "Cette valeur est synchronisée avec {{integrationName}} et ne peut pas être modifiée manuellement.",
        lockedTooltipGeneric: "Cette valeur est synchronisée automatiquement et ne peut pas être modifiée manuellement.",
        tag: {
            placeholder: "Ajouter un élément",
            duplicate: "Cet élément a déjà été ajouté.",
            remove: "Retirer {{tag}}"
        },
        person: {
            placeholder: "Rechercher une personne",
            noResults: "Aucune personne ne correspond à votre recherche",
            searchError: "Impossible de charger les personnes. Veuillez réessayer.",
            loading: "Recherche de personnes",
            loadingMore: "Chargement d'autres personnes",
            liveRegion: {
                loadedMore_one: "{{count}} personne de plus chargée",
                loadedMore_other: "{{count}} personnes de plus chargées"
            }
        }
    },
    PersonDetailHeaderActions: {
        deactivate: "Désactiver l'utilisateur",
        reactivate: "Réactiver l'utilisateur",
        sendInvite: "Envoyer l'invitation",
        resendInvite: "Renvoyer l'invitation",
        remove: "Supprimer l'utilisateur"
    },
    PersonNotFound: {
        heading: "Cette personne n'existe pas ou a été supprimée.",
        backToManagePeople: "Retour à Gérer les personnes"
    },
    PersonDetailErrorBoundary: {
        title: "Nous n'avons pas pu charger cette personne.",
        description: "Veuillez réessayer plus tard.",
        retry: "Réessayer"
    },
    PeopleFieldsPage: {
        description: "Les champs des employés peuvent être gérés dans Workleap Classic."
    },
    RolesAndPermissionsPage: {
        description: "Les rôles et permissions peuvent être gérés dans Workleap Classic."
    },
    AdminConnectedAppsKnowledgeSourcesPage: {
        description: "Choisissez les sources sur lesquelles Workleap AI peut s'appuyer pour répondre aux questions."
    },
    UploadedFilesCard: {
        heading: "Fichiers téléversés",
        description: "Importez de la documentation sur laquelle Workleap AI pourra s'appuyer pour répondre aux questions. Vous pouvez téléverser jusqu'à {{max}} fichiers à la fois.",
        uploadButton: "Téléverser un fichier",
        ariaLabel: "Fichiers téléversés",
        disclosure: {
            title_one: "{{count}} fichier téléversé",
            title_other: "{{count}} fichiers téléversés"
        },
        columns: {
            fileName: "Nom du fichier",
            uploadedBy: "Téléversé par",
            uploadedAt: "Date de téléversement",
            status: "Statut",
            actions: "Actions"
        },
        status: {
            available: "Disponible",
            processing: "En traitement",
            indexingFailed: "Indexation échouée",
            uploading: "Téléversement"
        },
        deleteFile: "Supprimer {{name}}",
        dismissFailure: "Masquer l'erreur pour {{name}}",
        uploadFailed: "{{name}} a échoué : {{reason}}",
        uploadFailure: {
            unsupportedType: "ce type de fichier n'est pas pris en charge.",
            tooLarge: "le fichier dépasse {{maxSize}} Mo.",
            unexpected: "une erreur inattendue est survenue."
        },
        maxFilesPerUpload: "Téléversement des {{max}} premiers fichiers sur {{total}}. Téléversez les autres séparément.",
        liveRegion: {
            loading: "Chargement des fichiers téléversés"
        },
        empty: {
            title: "Aucun fichier téléversé.",
            description: "Téléversez un document et Workleap AI utilisera son contenu pour répondre aux questions."
        },
        loadError: {
            title: "Impossible de charger les fichiers téléversés",
            retry: "Réessayer"
        },
        deletedToast: "Fichier supprimé.",
        download: {
            error: "Impossible de télécharger le fichier. Réessayez."
        },
        delete: {
            title: "Supprimer ce fichier?",
            description: "Une fois supprimé, Workleap AI ne pourra plus utiliser ce fichier pour répondre aux questions.",
            confirm: "Supprimer",
            cancel: "Annuler",
            error: "Impossible de supprimer le fichier. Réessayez."
        },
        leaveWhileUploading: {
            title: "Quitter pendant le téléversement?",
            description: "Les téléversements non terminés ne seront pas enregistrés.",
            confirm: "Quitter",
            cancel: "Poursuivre le téléversement"
        }
    },
    WebsitesCard: {
        heading: "Sites web",
        description: "Utilisez le contenu de sites web publics pour enrichir les réponses et les analyses.",
        addButton: "Ajouter un site web",
        ariaLabel: "Sites web approuvés",
        faviconAlt: "Icône de {{name}}",
        disclosure: {
            title_one: "{{count}} site web",
            title_other: "{{count}} sites web"
        },
        columns: {
            website: "Site web",
            status: "Statut",
            actions: "Actions"
        },
        status: {
            available: "Disponible"
        },
        rowMenu: {
            ariaLabel: "Actions pour {{name}}",
            edit: "Modifier",
            delete: "Supprimer"
        },
        liveRegion: {
            loading: "Chargement des sites web"
        },
        empty: {
            title: "Aucun site web ajouté",
            description: "Ajoutez un site web public et Workleap AI utilisera son contenu pour répondre aux questions."
        },
        loadError: {
            title: "Impossible de charger les sites web",
            retry: "Réessayer"
        },
        addedToast: "Site web ajouté.",
        updatedToast: "Site web mis à jour.",
        deletedToast: "Site web supprimé.",
        delete: {
            title: "Supprimer ce site web?",
            description: "Workleap AI cessera d'utiliser le contenu de {{name}} pour répondre aux questions.",
            confirm: "Supprimer",
            error: "Impossible de supprimer le site web. Réessayez."
        },
        form: {
            subtitle: "Workleap AI explore les pages situées sous cette adresse.",
            publicOnlyCallout: "Seuls les sites web accessibles publiquement sont pris en charge. Les sites protégés par une connexion, un VPN ou un pare-feu ne seront pas accessibles.",
            cancel: "Annuler",
            add: {
                title: "Ajouter un site web",
                submit: "Ajouter le site web"
            },
            edit: {
                title: "Modifier le site web",
                submit: "Enregistrer les modifications"
            },
            url: {
                label: "URL principale du site web",
                placeholder: "https://exemple.com",
                invalid: "L'URL est invalide, assurez-vous d'inclure http:// ou https://.",
                duplicate: "Ce site web a déjà été ajouté."
            },
            name: {
                label: "Nom affiché",
                placeholder: "Facultatif"
            },
            error: {
                save: "Impossible d'enregistrer le site web. Réessayez."
            }
        }
    },
    CustomMcpServersCard: {
        heading: "Serveurs MCP personnalisés",
        description: "Connectez vos propres serveurs MCP distants pour donner à Workleap AI accès à leurs outils.",
        addButton: "Ajouter un serveur",
        ariaLabel: "Serveurs MCP personnalisés",
        disclosure: {
            title_one: "{{count}} serveur",
            title_other: "{{count}} serveurs"
        },
        columns: {
            server: "Serveur",
            description: "Description",
            actions: "Actions"
        },
        rowMenu: {
            ariaLabel: "Actions pour {{name}}",
            edit: "Modifier",
            delete: "Supprimer"
        },
        liveRegion: {
            loading: "Chargement des serveurs MCP personnalisés"
        },
        empty: {
            title: "Aucun serveur MCP personnalisé ajouté",
            description: "Ajoutez un serveur MCP distant et Workleap AI utilisera ses outils pour répondre aux questions."
        },
        loadError: {
            title: "Impossible de charger les serveurs MCP personnalisés",
            retry: "Réessayer"
        },
        addedToast: "Serveur ajouté.",
        updatedToast: "Serveur mis à jour.",
        deletedToast: "Serveur supprimé.",
        delete: {
            title: "Supprimer {{name}}?",
            description: "Workleap AI ne pourra plus utiliser les outils de ce serveur, et les jetons d'accès et enregistrements d'applications de tous les membres pour ce serveur seront définitivement supprimés. Cette action est irréversible.",
            confirm: "Supprimer",
            error: "Impossible de supprimer le serveur. Réessayez."
        },
        form: {
            cancel: "Annuler",
            oauthCallout: "Seuls les serveurs prenant en charge OAuth sont acceptés. Le serveur doit exposer un document de découverte OAuth standard et être accessible depuis l'internet public.",
            add: {
                title: "Ajouter un serveur",
                subtitle: "Connectez un serveur MCP distant pour mettre ses outils à disposition de Workleap AI.",
                submit: "Ajouter le serveur"
            },
            edit: {
                title: "Modifier le serveur",
                subtitle: "Seuls le nom affiché et la description peuvent être modifiés après l'enregistrement.",
                submit: "Enregistrer les modifications"
            },
            url: {
                label: "URL du serveur",
                placeholder: "https://exemple.com/mcp",
                required: "L'URL du serveur est obligatoire.",
                invalid: "L'URL est invalide. Assurez-vous d'inclure https://.",
                httpsRequired: "L'URL du serveur doit utiliser HTTPS.",
                rejected: "Le serveur a été rejeté. Assurez-vous qu'il prend en charge OAuth et qu'il est accessible depuis l'internet public."
            },
            displayName: {
                label: "Nom affiché",
                required: "Le nom affiché est obligatoire."
            },
            description: {
                label: "Description",
                required: "La description est obligatoire."
            },
            clientId: {
                label: "Identifiant client (facultatif)"
            },
            clientSecret: {
                label: "Secret client (facultatif)",
                requiresClientId: "Un secret client nécessite un identifiant client."
            },
            error: {
                save: "Impossible d'enregistrer le serveur. Réessayez.",
                notFound: "Ce serveur n'existe plus. Fermez cette boîte de dialogue et actualisez la liste."
            }
        }
    },
    AdminConnectedAppsAccessPage: {
        description: "Les accès Slack, Microsoft Teams et ChatGPT seront gérés ici."
    },
    IntegrationsPage: {
        description: "Les intégrations peuvent être gérées dans Workleap Classic."
    },
    BillingPage: {
        description: "Les détails de facturation et de forfait peuvent être gérés dans Workleap Classic."
    },
    SegmentsHeading: {
        description: "Des filtres réutilisables qui restreignent les rapports et le répertoire à un groupe de personnes précis. Les segments basés sur des règles se mettent à jour d'eux-mêmes.",
        newSegment: "Nouveau segment"
    },
    SegmentsList: {
        selectAllMatching: "Sélectionner les {{count}} segments",
        deletedToast_one: "Segment supprimé.",
        deletedToast_other: "{{count}} segments supprimés.",
        deleteErrorToast: "Impossible de supprimer les segments. Veuillez réessayer.",
        renamedToast: "Segment renommé",
        renameErrorToast: "Impossible de renommer ce segment. Réessayez.",
        rowDeletedToast: "Segment supprimé",
        rowDeleteErrorToast: "Impossible de supprimer ce segment. Réessayez.",
        empty: {
            noResults: "Aucun segment ne correspond à « {{term}} ».",
            mine: {
                title: "Aucun segment pour l'instant",
                description: "Utilisez Nouveau segment pour définir un groupe de personnes réutilisable."
            },
            shared: {
                title: "Rien ne vous a encore été partagé",
                description: "Les segments que d'autres personnes partagent avec vous apparaîtront ici."
            }
        },
        error: {
            title: "Impossible de charger les segments.",
            description: "Veuillez réessayer plus tard.",
            retry: "Réessayer",
            refreshBanner: "Impossible d'actualiser — affichage des derniers résultats."
        },
        liveRegion: {
            loading: "Chargement des segments",
            loadingMoreAnnouncement: "Chargement d'autres segments",
            loadedMore_one: "{{count}} segment supplémentaire chargé",
            loadedMore_other: "{{count}} segments supplémentaires chargés",
            results_one: "{{count}} segment",
            results_other: "{{count}} segments"
        }
    },
    SegmentsListToolbar: {
        searchPlaceholder: "Rechercher des segments...",
        searchAriaLabel: "Rechercher des segments",
        tagsFilter: {
            label: "Toutes les étiquettes",
            labelSelected_one: "{{count}} étiquette",
            labelSelected_other: "{{count}} étiquettes",
            ariaLabel: "Filtrer par étiquette",
            clearFilter: "Effacer les étiquettes"
        }
    },
    SegmentsSelectionBar: {
        selectedCount_one: "{{count}} sélectionné",
        selectedCount_other: "{{count}} sélectionnés",
        delete: "Supprimer",
        clear: "Effacer"
    },
    DeleteSegmentsDialog: {
        title_one: "Supprimer ce segment?",
        title_other: "Supprimer {{count}} segments?",
        body: "La suppression retire ces segments. Les personnes qu'ils contiennent et leurs données ne sont pas touchées. Cette action est irréversible.",
        confirm: "Supprimer",
        cancel: "Annuler"
    },
    DeleteSegmentDialog: {
        title: "Supprimer « {{name}} »?",
        body: "Les personnes avec qui il est partagé en perdront l'accès. Cette action est irréversible.",
        confirm: "Supprimer",
        cancel: "Annuler"
    },
    DeleteJobDialog: {
        title: "Supprimer « {{title}} »?",
        body: "Cette action est irréversible.",
        confirm: "Supprimer",
        cancel: "Annuler"
    },
    SegmentRowMenu: {
        ariaLabel: "Actions pour {{name}}",
        rename: "Renommer",
        delete: "Supprimer"
    },
    RenameSegmentDialog: {
        title: "Renommer le segment",
        name: {
            label: "Nom du segment",
            placeholder: "Nommez ce segment",
            required: "Entrez un nom pour ce segment.",
            tooLong: "Limitez le nom à {{count}} caractères."
        },
        save: "Enregistrer",
        cancel: "Annuler"
    },
    SegmentsTableView: {
        ariaLabel: "Segments",
        columns: {
            name: "Nom",
            tags: "Étiquettes",
            owner: "Propriétaire",
            actions: "Actions"
        }
    },
    SkillLibraryPage: {
        description: "Ajustez la façon dont l'agent exécute chaque compétence pour votre organisation.",
        liveRegion: {
            loading: "Chargement des compétences"
        },
        features: {
            meetings: { label: "Réunions" },
            engagement: { label: "Engagement" },
            recognition: { label: "Bonnes Vibes" },
            performance_reviews: { label: "Évaluations de performance" },
            people: { label: "Parcours employé" },
            plans: { label: "Plans" }
        }
    },
    agentSkills: {
        skills: {
            wov_engagement_report: {
                name: "Rapport Pulse",
                description: "Envoie un résumé hebdomadaire de l'évolution de l'engagement de votre équipe."
            },
            wov_help_me_reply_to_feedback: {
                name: "M'aider à répondre au feedback",
                description: "Rédige une réponse réfléchie au feedback d'un employé."
            },
            wpm_review: {
                name: "Évaluation de performance",
                description: "Rassemble les données pour une évaluation, puis rédige, retravaille et affine les réponses en respectant votre style."
            },
            wai_people_about: {
                name: "À propos d'une personne",
                description: "Rédige un court profil sourcé d'une personne, limité à ce que le lecteur peut voir."
            },
            draft_a_plan: {
                name: "Rédiger un plan",
                description: "Construit le plan qu'un moment appelle : intégration, départ, retour de congé, amélioration, développement ou relève."
            },
            wov_summarize_feedback: {
                name: "Résumer le feedback",
                description: "Cible les thèmes de votre feedback, en explique l'importance et suggère des actions concrètes."
            },
            wov_feedback_export: {
                name: "Exporter le rapport de feedback",
                description: "Transforme votre rapport de feedback en présentation ou en document prêt à partager."
            },
            wov_analyze_engagement: {
                name: "Analyser l'engagement",
                description: "Cible ce qui se dégage de vos résultats d'engagement, d'eNPS et de participation, en explique la cause et suggère les prochaines étapes."
            },
            wov_export_engagement: {
                name: "Exporter le rapport d'engagement",
                description: "Transforme votre rapport d'engagement en présentation ou en document prêt à partager."
            },
            wpm_prepare_meeting_agenda: {
                name: "Préparer un ordre du jour",
                description: "Expose où en est la personne, puis ce qu'il faut aborder — chaque point relié à sa source."
            },
            wpm_summarize_meeting: {
                name: "Récapitulatif de réunion",
                description: "Rédige le récapitulatif d'un 1:1 à partir de ce que les applications de prise de notes ont enregistré, sans conserver la transcription."
            },
            wpm_organize_meetings: {
                name: "Organiser les réunions",
                description: "Trouve une plage qui convient à tous, la réserve, et signale les 1:1 manquants ou en retard."
            }
        }
    },
    SkillDetailPage: {
        personalize: {
            title: "Personnaliser",
            description: "Le comportement de l'agent lorsqu'il utilise cette compétence.",
            byDefault: "Modèles de compétence",
            byDefaultDescription: "Chacun est un ensemble d'instructions différent que l'agent peut suivre. Tout ce que vous ajoutez devient accessible à toute votre équipe.",
            memberAccessLabel: "Accès des membres"
        },
        liveRegion: {
            loading: "Chargement de la compétence"
        },
        makeDefault: {
            savedToast: "Modèle par défaut mis à jour.",
            errorToast: "Impossible de mettre à jour le modèle par défaut."
        }
    },
    SkillPersonalizationToggle: {
        label: "Permettre aux gens de créer leurs propres modèles pour cette compétence",
        savedToast: "Paramètre de personnalisation enregistré.",
        errorToast: "Impossible d'enregistrer le paramètre de personnalisation."
    },
    SkillTemplates: {
        empty: "Aucun modèle pour l'instant. Créez-en un pour ajouter une autre façon d'utiliser cette compétence.",
        newTemplate: "Nouveau modèle",
        newTemplateName: "Nouveau modèle",
        makeDefault: "Définir par défaut",
        defaultLabel: "Par défaut",
        toggleCard: "Afficher ou masquer {{name}}",
        scopeWorkleap: "Workleap",
        scopeOrganization: "Votre organisation",
        showAll_one: "Afficher {{count}} autre modèle",
        showAll_other: "Afficher {{count}} autres modèles",
        showFewer: "Afficher moins",
        standardTemplate: "Modèle standard",
        standardTemplates: {
            wov_engagement_report: {
                name: "Rapport éclair standard"
            },
            wov_help_me_reply_to_feedback: {
                name: "Réponse standard au feedback"
            },
            wpm_review: {
                name: "Équilibrée et fidèle à notre style"
            },
            wai_people_about: {
                name: "Présentation standard"
            }
        }
    },
    SkillResourcePanel: {
        loading: "Chargement du modèle",
        error: "Impossible de charger ce modèle.",
        retry: "Réessayer"
    },
    SkillTemplateEditor: {
        nameLabel: "Nom",
        namePlaceholder: "Nommez ce modèle",
        requiredName: "Entrez un nom de modèle.",
        nameTooLong: "Limitez le nom à {{count}} caractères.",
        contentLabel: "Instructions de l'agent",
        contentDescription: "Comment l'agent doit exécuter cette compétence lorsqu'il utilise ce modèle.",
        contentPlaceholder: "Rédigez les consignes que l'agent doit suivre, en langage clair.",
        requiredContent: "Entrez les instructions du modèle.",
        contentTooLong: "Limitez les instructions à {{count}} caractères.",
        cancelNew: "Annuler",
        discard: "Abandonner",
        create: "Créer le modèle",
        saveChanges: "Enregistrer les modifications",
        delete: "Supprimer",
        cancel: "Annuler",
        deleteTitle: "Supprimer ce modèle ?",
        deleteBody: "Il ne sera plus accessible à l'agent. Cette action est irréversible.",
        createdToast: "Modèle créé.",
        createErrorToast: "Impossible de créer le modèle.",
        savedToast: "Modèle enregistré.",
        saveErrorToast: "Impossible d'enregistrer le modèle.",
        deletedToast: "Modèle supprimé.",
        deleteErrorToast: "Impossible de supprimer le modèle."
    },
    OrganizationInstructionsEditor: {
        contentLabel: "Consignes de l'organisation",
        contentDescription: "Ajoutées par-dessus le modèle utilisé, y compris ceux que vous créez. Laissez le champ vide pour les retirer.",
        contentPlaceholder: "Rédigez les consignes que l'agent doit suivre, en langage clair.",
        contentTooLong: "Limitez les instructions à {{count}} caractères.",
        discard: "Abandonner",
        saveChanges: "Enregistrer les modifications",
        savedToast: "Consignes de l'organisation enregistrées.",
        saveErrorToast: "Impossible d'enregistrer les consignes de l'organisation."
    },
    SkillFeatureInstructions: {
        title: "Instructions de la fonctionnalité",
        description: "En lecture seule ici. Ces instructions s'appliquent à tous les modèles de cette compétence.",
        organizationTitle: "Garde-fous de l'organisation",
        organizationDescription: "Hérités des paramètres de cette fonctionnalité.",
        emptyOrganization: "Aucun garde-fou d'organisation n'est configuré.",
        organizationUnavailable: "Impossible de charger les garde-fous de l'organisation.",
        liveRegion: {
            loading: "Chargement des instructions de la fonctionnalité"
        }
    },
    JobsPage: {
        description: "Les postes peuvent être gérés dans Workleap Classic."
    },
    JobArchitecture: {
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            jobs: "Postes",
            jobArchitecture: "Architecture"
        },
        title: "Architecture des postes",
        description: "Les fonctions regroupent vos postes selon le type de travail qu'ils impliquent. Les niveaux définissent l'ancienneté. Ensemble, ils déterminent les échelles salariales, les évaluations de performance et les parcours de carrière.",
        actions: {
            discard: "Annuler",
            save: "Enregistrer les modifications"
        },
        nameRequired: "Le nom est requis.",
        nameDuplicate: "Une autre nouvelle ligne utilise déjà ce nom.",
        nameAlreadyInUse: "Ce nom est déjà utilisé.",
        saveErrorRow: "Impossible d'enregistrer. Réessayez.",
        saveSuccessToast: "Architecture des postes mise à jour.",
        saveErrorToast: "Certaines modifications n'ont pas pu être enregistrées.",
        editFunctionSuccessToast: "Fonction mise à jour.",
        editFunctionErrorToast: "Impossible de mettre à jour la fonction. Réessayez.",
        editFunctionNotFoundToast: "Cette fonction n'existe plus.",
        editLevelSuccessToast: "Niveau mis à jour.",
        editLevelErrorToast: "Impossible de mettre à jour le niveau. Réessayez.",
        editLevelNotFoundToast: "Ce niveau n'existe plus."
    },
    JobFunctionsCard: {
        title: "Fonctions",
        description: "Regroupez vos postes selon le type de travail qu'ils impliquent.",
        columns: {
            name: "Nom",
            jobs: "Postes"
        },
        jobsCount_one: "{{count}} poste",
        jobsCount_other: "{{count}} postes",
        noJobs: "Aucun poste",
        jobsCountAriaLabel_one: "{{count}} poste dans {{name}}",
        jobsCountAriaLabel_other: "{{count}} postes dans {{name}}",
        addFunction: "Ajouter une fonction",
        deleteFunction: "Supprimer {{name}}",
        draftNameAriaLabel: "Nom de la nouvelle fonction {{position}}",
        removeDraft: "Retirer la nouvelle fonction {{position}}",
        empty: "Aucune fonction pour le moment.",
        loadError: {
            title: "Impossible de charger les fonctions.",
            retry: "Réessayer",
            retrying: "Nouvelle tentative"
        },
        liveRegion: {
            loading: "Chargement des fonctions"
        }
    },
    JobLevelsCard: {
        title: "Niveaux",
        description: "Définissez l'ancienneté selon la portée, la complexité et l'impact.",
        columns: {
            number: "#",
            name: "Nom",
            individualContributor: "Collaborateur individuel",
            manager: "Gestionnaire"
        },
        nameAriaLabel: "Nom du niveau {{number}}",
        individualContributorAriaLabel: "Filière collaborateur individuel pour {{name}}",
        managerAriaLabel: "Filière gestionnaire pour {{name}}",
        draftIndividualContributorAriaLabel: "Filière collaborateur individuel pour le nouveau niveau {{position}}",
        draftManagerAriaLabel: "Filière gestionnaire pour le nouveau niveau {{position}}",
        draftNameAriaLabel: "Nom du nouveau niveau {{position}}",
        addLevel: "Ajouter un niveau",
        deleteLevel: "Supprimer {{name}}",
        removeDraft: "Retirer le nouveau niveau {{position}}",
        empty: "Aucun niveau pour le moment.",
        loadError: {
            title: "Impossible de charger les niveaux.",
            retry: "Réessayer",
            retrying: "Nouvelle tentative"
        },
        liveRegion: {
            loading: "Chargement des niveaux"
        }
    },
    JobsList: {
        manageJobArchitecture: "Gérer l'architecture des postes",
        addJobs: "Ajouter des postes",
        totalCount_one: "{{count}} poste",
        totalCount_other: "{{count}} postes",
        selectAllMatching_one: "Sélectionner le {{count}} poste correspondant",
        selectAllMatching_other: "Sélectionner les {{count}} postes correspondants",
        noResults: "Aucun poste ne correspond à vos filtres",
        empty: {
            title: "Votre architecture est prête. Ajoutez maintenant des postes.",
            description: "Les postes s'inscrivent dans les fonctions et les niveaux que vous venez de configurer. Une fois ajoutés, vous pourrez les utiliser dans les évaluations de performance et les cycles de rémunération.",
            addJob: "Ajouter un poste"
        },
        error: {
            title: "Impossible de charger les postes.",
            description: "Veuillez réessayer plus tard.",
            retry: "Réessayer",
            refreshBanner: "Impossible d'actualiser - affichage des derniers résultats."
        },
        liveRegion: {
            loading: "Chargement des postes...",
            loadedMore: "{{count}} postes supplémentaires chargés.",
            results: "{{count}} postes chargés.",
            sortedAscending: "Trié par {{column}}, ordre croissant.",
            sortedDescending: "Trié par {{column}}, ordre décroissant."
        }
    },
    JobsToolbar: {
        searchPlaceholder: "Rechercher par titre",
        searchAriaLabel: "Rechercher des postes",
        statusFilter: {
            label: "Statut",
            ariaLabel: "Filtrer par statut",
            reset: "Rétablir par défaut"
        },
        functionFilter: {
            label: "Fonction",
            ariaLabel: "Filtrer par fonction",
            clearAriaLabel: "Effacer le filtre de fonction",
            any: "Toutes",
            deleted: "Fonction supprimée"
        },
        levelFilter: {
            label: "Niveau",
            ariaLabel: "Filtrer par niveau",
            clearAriaLabel: "Effacer le filtre de niveau",
            any: "Tous"
        },
        resetFilters: "Réinitialiser les filtres"
    },
    JobsTable: {
        ariaLabel: "Postes",
        columns: {
            title: "Titre",
            function: "Fonction",
            level: "Niveau",
            people: "Personnes",
            status: "Statut"
        },
        status: {
            draft: "Brouillon",
            active: "Actif",
            archived: "Archivé"
        },
        noFunction: "-",
        noLevel: "-"
    },
    JobsSelectionBar: {
        selectedCount_one: "{{count}} sélectionné",
        selectedCount_other: "{{count}} sélectionnés",
        clear: "Effacer",
        edit: "Modifier",
        setFunction: "Définir la fonction",
        setLevel: "Définir le niveau",
        setTrack: "Définir la filière",
        addCompetency: "Ajouter une compétence"
    },
    JobsBulkEditDialog: {
        function: {
            title_one: "Définir la fonction de {{count}} poste",
            title_other: "Définir la fonction de {{count}} postes",
            label: "Fonction",
            placeholder: "Sélectionnez une fonction",
            submit_one: "Définir la fonction de {{count}} poste",
            submit_other: "Définir la fonction de {{count}} postes"
        },
        level: {
            title_one: "Définir le niveau de {{count}} poste",
            title_other: "Définir le niveau de {{count}} postes",
            description_one: "Attribuez une filière et un niveau au poste sélectionné.",
            description_other: "Attribuez une filière et un niveau aux postes sélectionnés.",
            label: "Niveau",
            placeholder: "Sélectionnez un niveau",
            trackNotSupported: "Cette filière n'est pas offerte à ce niveau.",
            submit_one: "Définir le niveau de {{count}} poste",
            submit_other: "Définir le niveau de {{count}} postes"
        },
        track: {
            title_one: "Définir la filière de {{count}} poste",
            title_other: "Définir la filière de {{count}} postes",
            label: "Filière",
            placeholder: "Sélectionnez une filière",
            options: {
                individualContributor: "Contributeur individuel",
                manager: "Gestionnaire"
            },
            submit_one: "Définir la filière de {{count}} poste",
            submit_other: "Définir la filière de {{count}} postes"
        },
        competencies: {
            title_one: "Ajouter une compétence à {{count}} poste",
            title_other: "Ajouter une compétence à {{count}} postes",
            label: "Compétences à ajouter",
            placeholder: "Ajouter une compétence",
            hint: "Elles sont ajoutées à chaque poste sélectionné. Les compétences déjà présentes sur un poste sont conservées.",
            submit_one: "Ajouter à {{count}} poste",
            submit_other: "Ajouter à {{count}} postes"
        },
        partialFailure: {
            updated_one: "{{count}} poste mis à jour.",
            updated_other: "{{count}} postes mis à jour.",
            skipped: {
                trackNotSupportedByLevel_one: "{{count}} poste a été ignoré parce que la filière choisie n'est pas offerte à son niveau.",
                trackNotSupportedByLevel_other: "{{count}} postes ont été ignorés parce que la filière choisie n'est pas offerte à leur niveau."
            }
        },
        successToast_one: "{{count}} poste mis à jour.",
        successToast_other: "{{count}} postes mis à jour.",
        errorToast: "Impossible de mettre à jour les postes sélectionnés. Veuillez réessayer.",
        validationErrorToast: "Ce changement ne peut pas être appliqué aux postes sélectionnés.",
        cancel: "Annuler"
    },
    JobFunctionDeleteDialog: {
        title: "Supprimer « {{name}} »?",
        confirmBody: "Voulez-vous vraiment supprimer « {{name}} »? Cette action est irréversible.",
        reassignBody_one: "{{count}} poste est associé à cette fonction, ce qui touche {{people}}. Réassignez-le pour continuer.",
        reassignBody_other: "{{count}} postes sont associés à cette fonction, ce qui touche {{people}}. Réassignez-les pour continuer.",
        reassignBodyWithoutPeople_one: "{{count}} poste est associé à cette fonction. Réassignez-le pour continuer.",
        reassignBodyWithoutPeople_other: "{{count}} postes sont associés à cette fonction. Réassignez-les pour continuer.",
        people_one: "{{count}} employé",
        people_other: "{{count}} employés",
        targetLabel: "Déplacer tous les postes vers",
        targetPlaceholder: "Sélectionnez une fonction",
        delete: "Supprimer",
        reassignAndDelete: "Réassigner et supprimer",
        cancel: "Annuler",
        successToast: "Fonction supprimée.",
        errorToast: "Impossible de supprimer la fonction. Réessayez.",
        validationErrorToast: "Cette fonction est encore associée à des postes. Réassignez-les et réessayez.",
        liveRegion: {
            loading: "Chargement des employés touchés"
        }
    },
    JobLevelDeleteDialog: {
        blockedTitle: "« {{name}} » ne peut pas être supprimé",
        blockedBody_one: "{{count}} poste est associé à ce niveau, ce qui touche {{people}}. Réassignez-le à un autre niveau avant de supprimer.",
        blockedBody_other: "{{count}} postes sont associés à ce niveau, ce qui touche {{people}}. Réassignez-les à un autre niveau avant de supprimer.",
        people_one: "{{count}} employé",
        people_other: "{{count}} employés",
        viewJobs_one: "Voir {{count}} poste",
        viewJobs_other: "Voir {{count}} postes",
        confirmTitle: "Supprimer le niveau?",
        confirmBody: "Voulez-vous vraiment supprimer « {{name}} »? Cette action est irréversible.",
        delete: "Supprimer",
        cancel: "Annuler",
        successToast: "Niveau supprimé.",
        errorToast: "Impossible de supprimer le niveau. Réessayez.",
        loadingLabel: "Vérification de l'utilisation de ce niveau",
        usageError: {
            title: "Impossible de vérifier où ce niveau est utilisé.",
            retry: "Réessayer",
            retrying: "Nouvelle tentative"
        }
    },
    JobArchitectureGetStartedPage: {
        eyebrow: "Pour commencer",
        label: "Configurez votre architecture de postes",
        description: "Configurez-la une seule fois : les échelles salariales, les évaluations et les parcours de carrière s'appuieront dessus.",
        recommended: "Recommandé",
        aiOption: {
            title: "Configurer avec l'IA",
            description: "Décrivez votre organisation ou téléversez un document, et l'agent rédige votre architecture.",
            link: "Configurer avec l'IA"
        },
        manualOption: {
            title: "Créer manuellement",
            description: "Créez vos fonctions, niveaux et filières étape par étape.",
            link: "Commencer la configuration"
        },
        addPeopleLink: "Ajoutez vos employés dans Workleap à tout moment"
    },
    JobArchitectureAiSetupWizard: {
        title: "Configurez votre architecture de postes",
        steps: {
            describe: "Téléversement",
            architecture: "Architecture",
            jobTitles: "Titres de poste"
        },
        stepperAriaLabel: "Progression de la configuration",
        close: "Fermer",
        liveRegion: {
            loading: "Élaboration de votre architecture de postes en cours…"
        },
        suggestedCount_one: "{{count}} fonction d'emploi suggérée.",
        suggestedCount_other: "{{count}} fonctions d'emploi suggérées.",
        jobMappingsSuggestedCount_one: "{{count}} correspondance de titre de poste générée.",
        jobMappingsSuggestedCount_other: "{{count}} correspondances de titres de poste générées.",
        architectureError: {
            timeout: {
                title: "Cela prend plus de temps que prévu.",
                description: "La demande a expiré avant qu'une architecture de postes ne soit générée."
            },
            noSuggestions: {
                title: "Aucun résultat.",
                description: "L'agent n'a généré aucune fonction d'emploi cette fois-ci."
            },
            generic: {
                title: "Impossible de déterminer l'architecture de postes.",
                description: "Une erreur est survenue. Veuillez réessayer."
            },
            retry: "Réessayer",
            retrying: "Nouvelle tentative",
            startOver: "Recommencer"
        },
        reviewingJobTitles: "Analyse de vos titres de poste en cours…",
        jobMappingsError: {
            timeout: {
                title: "Cela prend plus de temps que prévu.",
                description: "La demande a expiré avant que les correspondances de titres de poste ne soient générées."
            },
            noSuggestions: {
                title: "Aucun résultat.",
                description: "L'agent n'a établi aucune correspondance de titres de poste cette fois-ci."
            },
            generic: {
                title: "Impossible de déterminer les correspondances de titres de poste.",
                description: "Une erreur est survenue. Veuillez réessayer."
            },
            retry: "Réessayer",
            retrying: "Nouvelle tentative",
            back: "Retour à l'architecture"
        },
        finishSetupSuccessToast_one: "{{count}} titre de poste enregistré comme brouillon.",
        finishSetupSuccessToast_other: "{{count}} titres de poste enregistrés comme brouillons.",
        finishSetupErrorToast: "Impossible de terminer la configuration. Veuillez réessayer."
    },
    JobArchitectureDescribeStep: {
        eyebrow: "Étape 1 sur 3",
        title: "Décrivez votre architecture de postes",
        description: "Dites à l'agent comment vos rôles sont organisés et il rédige vos fonctions et vos niveaux. Ajoutez vos documents ci-dessous.",
        addFiles: "Ajouter des fichiers",
        addFilesHelp: "Guides de niveaux, référentiels de carrière ou grille des emplois (PDF, Word, Excel, CSV). Un SIRH importe aussi vos titres et vos employés.",
        maxFilesTooltip: "Vous pouvez ajouter jusqu'à 5 fichiers.",
        selectedFilesLabel: "Fichiers sélectionnés",
        suggestPrompt: "Vous ne savez pas par où commencer?",
        suggestLink: "Suggérer une architecture",
        buildButton: "Créer mon architecture de postes"
    },
    JobHeader: {
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            jobs: "Postes",
            editJob: "Modifier le poste"
        },
        title: {
            placeholder: "Titre du poste"
        },
        actions: {
            label: "Actions du poste",
            generateDescription: "Générer une description avec l'IA",
            archive: "Archiver",
            delete: "Supprimer"
        }
    },
    JobPage: {
        title: {
            required: "Le titre est obligatoire",
            duplicate: "Un poste portant ce titre existe déjà."
        },
        function: {
            required: "Une fonction est requise",
            createErrorToast: "Impossible de créer la fonction. Veuillez réessayer."
        },
        liveRegion: {
            loading: "Chargement du poste...",
            submitBlocked: "Ce poste ne peut pas encore être enregistré. Vérifiez les champs signalés."
        },
        saveErrorToast: "Échec de l'enregistrement du poste. Veuillez réessayer.",
        saveSuccessToast: "Poste enregistré avec succès.",
        statusErrorToast: "Poste enregistré, mais la modification du statut a échoué. Veuillez réessayer.",
        statusOnlyErrorToast: "Échec de la modification du statut. Veuillez réessayer.",
        unsavedChanges: {
            title: "Quitter sans enregistrer?",
            body: "Vos modifications seront perdues si vous quittez cette page.",
            stayButton: "Continuer la modification",
            leaveButton: "Abandonner les modifications"
        },
        archiveSuccessToast: "Poste archivé.",
        archiveBlockedToast: "Ce poste ne peut pas être archivé tant que des personnes y sont assignées.",
        archiveErrorToast: "Échec de l'archivage du poste. Veuillez réessayer.",
        deleteSuccessToast: "Poste supprimé.",
        deleteBlockedToast: "Ce poste ne peut pas être supprimé, car il figure dans l'historique d'emploi d'un ou plusieurs employés.",
        deleteErrorToast: "Échec de la suppression du poste. Veuillez réessayer.",
        generateDescription: {
            successToast: "Description rédigée avec l'IA.",
            errorToast: "Impossible de générer une description. Veuillez réessayer.",
            timeoutErrorToast: "Cela prend plus de temps que prévu. Veuillez réessayer.",
            noDescriptionErrorToast: "L'IA n'a pas pu générer de description pour ce poste. Veuillez réessayer.",
            contextChangedToast: "Les détails du poste ont changé pendant la génération. Veuillez réessayer."
        },
        actions: {
            discard: "Annuler",
            update: "Mettre à jour"
        }
    },
    JobFormFields: {
        description: {
            label: "Description",
            placeholder: "Décrivez les responsabilités de ce poste.",
            aiGeneratingBanner: "Génération d'une description avec l'IA…",
            aiDraftedBanner: "Rédigé par l'IA — à vérifier avant d'enregistrer.",
            undo: "Rétablir",
            dismiss: "Fermer"
        },
        competencies: {
            label: "Compétences",
            placeholder: "Ajouter une compétence"
        }
    },
    JobFunctionComboBox: {
        label: "Fonction",
        placeholder: "Sélectionnez une fonction",
        noMatches: "Aucune fonction correspondante",
        create: 'Créer "{{name}}"'
    },
    JobFormSidebar: {
        track: {
            label: "Filière",
            placeholder: "Sélectionnez une filière",
            options: {
                individualContributor: "Contributeur individuel",
                manager: "Gestionnaire"
            }
        },
        level: {
            label: "Niveau",
            placeholder: "Sélectionnez un niveau",
            none: "Aucun",
            trackNotSupported: "Cette filière n'est pas offerte à ce niveau."
        },
        people: {
            label: "Personnes",
            none: "Aucune",
            count_one: "{{count}} personne",
            count_other: "{{count}} personnes"
        },
        status: {
            label: "Statut",
            placeholder: "Sélectionnez un statut",
            options: {
                draft: "Brouillon",
                active: "Actif",
                archived: "Archivé"
            }
        },
        lastUpdated: "Mis à jour {{timeAgo}}",
        relativeTime: {
            today: "aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours",
            weeks_one: "il y a {{count}} semaine",
            weeks_other: "il y a {{count}} semaines"
        }
    },
    NewJobPage: {
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            jobs: "Postes",
            newJob: "Nouveau poste"
        },
        title: {
            placeholder: "Titre du poste",
            required: "Le titre est obligatoire",
            duplicate: "Un poste portant ce titre existe déjà."
        },
        function: {
            required: "Une fonction est requise pour enregistrer ce poste",
            createErrorToast: "Impossible de créer la fonction. Veuillez réessayer."
        },
        liveRegion: {
            loading: "Chargement des options...",
            submitBlocked: "Ce poste ne peut pas encore être enregistré. Vérifiez les champs signalés."
        },
        saveErrorToast: "Échec de l'enregistrement du poste. Veuillez réessayer.",
        saveSuccessToast: "Poste enregistré avec succès.",
        actions: {
            discard: "Annuler",
            saveAsDraft: "Enregistrer comme brouillon",
            activate: "Activer"
        }
    },
    JobNotFound: {
        heading: "Poste introuvable",
        body: "Ce poste n'existe pas ou n'est plus disponible.",
        backToJobs: "Retour aux postes"
    },
    JobArchitectureLoadingSteps: {
        title: "Élaboration de votre architecture",
        titleJobMappings: "Analyse de vos titres de poste",
        steps: {
            readingDocument: "Lecture de votre document",
            extractingFunctions: "Extraction des fonctions d'emploi",
            extractingLevels: "Extraction des niveaux d'emploi",
            preparingForReview: "Préparation des fonctions et niveaux pour votre révision",
            jobMappings: {
                matchingTitles: "Analyse de vos titres de poste",
                checkingConfidence: "Vérification de la fiabilité des correspondances",
                preparingForReview: "Préparation des correspondances de titres de poste pour votre révision"
            }
        }
    },
    JobArchitectureEditStep: {
        eyebrow: "Étape 2 sur 3",
        title: "Architecture",
        description: "Chaque poste est associé à une fonction, un niveau et une filière. Configurez ces éléments de base ici.",
        jobFunctionsTitle: "Fonctions",
        jobFunctionsDescription: "Une fonction regroupe des postes similaires, comme Ingénierie ou Développement des affaires.",
        jobLevelsTitle: "Niveaux",
        jobLevelsDescription: "Un niveau peut s'appliquer à la filière de contributeur individuel, à la filière de gestion, ou aux deux.",
        columns: {
            functionName: "Nom de la fonction",
            number: "#",
            level: "Niveau",
            individualContributor: "Contributeur individuel",
            manager: "Gestionnaire",
            individualContributorFor: "Filière contributeur individuel pour {{name}}",
            managerFor: "Filière gestionnaire pour {{name}}"
        },
        addFunction: "Ajouter une fonction",
        deleteFunction: "Supprimer {{name}}",
        addLevel: "Ajouter un niveau",
        deleteLevel: "Supprimer {{name}}",
        reviewJobTitles: "Analyser les titres de poste",
        reviewJobTitlesDisabledHint: "Donnez un nom unique à chaque fonction et à chaque niveau pour continuer."
    },
    JobArchitectureJobTitlesStep: {
        eyebrow: "Étape 3 sur 3",
        title: "Titres de poste",
        description: "Chaque titre est associé à une fonction, un niveau et une filière. Rejetez ce qui est incorrect; le reste est enregistré comme brouillon que vous activez quand vous êtes prêt.",
        approved: "approuvés",
        toReview: "à examiner",
        rejected: "rejetés",
        mappingsTitle: "Haute confiance",
        empty: "Aucune correspondance de titre de poste n'a été générée.",
        needsReviewTitle: "À vérifier",
        columns: {
            jobTitle: "Titre",
            function: "Fonction",
            level: "Niveau",
            track: "Filière",
            functionFor: "Fonction pour {{name}}",
            levelFor: "Niveau pour {{name}}",
            trackFor: "Filière pour {{name}}"
        },
        individualContributor: "CI",
        manager: "Gestionnaire",
        approve: "Approuver",
        approvedLabel: "Approuvé",
        approveAriaLabel: "Approuver {{name}}",
        approvedAriaLabel: "Approuvé {{name}}",
        approveAll: "Tout approuver ({{count}})",
        reject: "Rejeter {{name}}",
        undoReject: "Annuler le rejet de {{name}}",
        finishSetup: "Terminer la configuration",
        finishSetupDisabledHint: "Passez en revue chaque titre de poste et approuvez-en au moins un pour terminer la configuration.",
        finishingSetup: "Configuration en cours…"
    },
    ManageReviewCyclesPage: {
        description: "Les cycles d'évaluation peuvent être créés et gérés dans Workleap Performance."
    },
    FeaturesPage: {
        loadError: {
            title: "Impossible de charger les fonctionnalités",
            description: "Veuillez réessayer plus tard.",
            retry: "Réessayer",
            retrying: "Nouvelle tentative"
        },
        liveRegion: {
            loading: "Chargement des fonctionnalités"
        }
    },
    FeaturesTable: {
        ariaLabel: "Fonctionnalités",
        columns: {
            feature: "Fonctionnalité",
            access: "Qui peut l'utiliser"
        },
        features: {
            engagement: {
                name: "Engagement",
                description: "Sondages pulse et canal de feedback"
            },
            goals: {
                name: "Objectifs",
                description: "Objectifs individuels, d'équipe et d'entreprise, et leur progression"
            },
            "good-vibes": {
                name: "Bonnes Vibes",
                description: "La reconnaissance que les gens s'envoient, liée à vos valeurs fondamentales"
            },
            meetings: {
                name: "Réunions",
                description: "Réunions 1:1 récurrentes entre les gestionnaires et leurs employés, avec ordres du jour et notes"
            },
            "performance-reviews": {
                name: "Évaluations de performance",
                description: "Cycles récurrents d'autoévaluation, d'évaluation par les pairs et par le gestionnaire, avec calibration"
            },
            plans: {
                name: "Plans",
                description: "Plans d'accueil, de départ et de développement assignés à une personne"
            }
        },
        access: {
            everyone: "Tout le monde",
            noOne: "Personne",
            partial_one: "{{included}} sur {{count}} personne",
            partial_other: "{{included}} sur {{count}} personnes"
        }
    },
    EngagementSettingsPage: {
        description: "Les paramètres d'Engagement sont gérés dans Workleap Classic.",
        openSettings: "Ouvrir les paramètres d'engagement"
    },
    GoodVibesSettingsPage: {
        description: "Les paramètres de Bonnes Vibes sont gérés dans Workleap Classic.",
        openSettings: "Ouvrir les paramètres de Bonnes Vibes"
    },
    ReviewCycleDetailHeading: {
        origin: {
            settings: "Gérer les cycles d'évaluation",
            home: "Accueil"
        }
    },
    NewReviewCyclePage: {
        creating: "Création de votre cycle d'évaluation",
        error: "Impossible de créer le cycle d'évaluation. Veuillez réessayer."
    },
    ReviewCycleBuilderPage: {
        loading: "Chargement de votre cycle d'évaluation"
    },
    CycleBuilderForm: {
        pageTitle: "Configuration du cycle d'évaluation",
        nav: {
            label: "Aller à une section",
            sectionHasError: "Requiert votre attention"
        },
        sections: {
            basics: "Informations de base",
            reviewForm: "Formulaire d'évaluation",
            schedule: "Calendrier",
            reviewees: "Personnes évaluées",
            reviewScore: "Score d'évaluation",
            nineBox: "Grille 9 cases",
            acknowledgment: "Prise de connaissance formelle",
            notifications: "Sommaire des notifications"
        },
        notificationsDescription: "Tout ce que ce cycle enverra, et à qui.",
        sectionDescriptions: {
            reviewScore: "Un seul chiffre, calculé à partir des réponses.",
            nineBox: "Situez chaque personne selon sa performance et son potentiel."
        },
        sectionPlaceholder: "Rien à configurer ici pour le moment.",
        name: {
            label: "Nom du cycle",
            placeholder: "p. ex. Évaluation de rendement S1 2026",
            required: "Saisissez un nom pour ce cycle."
        },
        description: {
            label: "Description",
            placeholder: "Expliquez aux évaluateurs l'objectif de ce cycle"
        },
        reviewTypes: {
            label: "Types d'évaluation"
        },
        acknowledgment: {
            label: "Les personnes évaluées doivent confirmer avoir lu leurs résultats",
            eligible: "Une fois la prise de connaissance faite, une évaluation ne peut plus être retirée du partage."
        },
        scheduleErrors: {
            evaluationEndBeforeStart: "La période d'évaluation doit se terminer à sa date de début ou après.",
            closeNotAfterOpen: "Le cycle doit se fermer après sa date d'ouverture.",
            closeNotAfterDueDate: "Le cycle doit se fermer après le {{date}}, sa dernière date limite de soumission.",
            closeNotAfterLastOpening: "Le cycle doit se fermer après l'ouverture de la dernière évaluation.",
            closeNotAfterFinalDueDate: "Le cycle doit se fermer après le {{date}}, la dernière échéance de la dernière personne évaluée.",
            dueDateBeforeOpen: {
                self: "L'échéance des auto-évaluations ne peut pas précéder le lancement du cycle.",
                manager: "L'échéance des évaluations par le gestionnaire ne peut pas précéder le lancement du cycle.",
                peer: "L'échéance des évaluations par les pairs ne peut pas précéder le lancement du cycle.",
                upward: "L'échéance des évaluations ascendantes ne peut pas précéder le lancement du cycle."
            }
        }
    },
    CycleScheduleSection: {
        type: {
            label: "Type de cycle",
            focal: {
                label: "Période fixe",
                description: "Tout le monde est évalué en même temps, dans une seule période partagée."
            },
            hireDate: {
                label: "Date d'embauche",
                description: "Chaque personne est évaluée à sa propre date."
            },
            basedOn: {
                label: "Selon",
                anniversary: "Anniversaire de la date d'embauche",
                milestone: "Seuil d'ancienneté"
            },
            grouping: {
                label: "Regroupement",
                none: "Aucun",
                weekly: "Cohortes hebdomadaires",
                monthly: "Cohortes mensuelles"
            },
            milestone: {
                label: "Seuil",
                "30": "Après 30 jours",
                "60": "Après 60 jours",
                "90": "Après 90 jours",
                "180": "Après 6 mois",
                "365": "Après 1 an"
            }
        },
        evaluationPeriod: {
            label: "Période d'évaluation",
            hint: "Le travail évalué.",
            startDate: "Date de début",
            endDate: "Date de fin",
            lengthLabel: "Durée de la période d'évaluation",
            lengthPrefix: "Évaluer les",
            sinceHirePrefix: "Évaluer",
            lengthSuffix: "qui précèdent cette date.",
            months_one: "{{count}} mois",
            months_other: "{{count}} mois",
            sinceHire: "tout depuis son embauche",
            placeholder: "Choisissez une durée"
        },
        mode: {
            automatic: "Automatiquement",
            manual: "Manuellement"
        },
        open: {
            focalLabel: "Lancement du cycle",
            hireDateLabel: "Ouverture des évaluations",
            focalDateLabel: "Date de lancement",
            hireDateDateLabel: "Date d'ouverture",
            stillManual: "Vous pourrez quand même lancer le cycle manuellement.",
            intake: {
                label: "Les évaluations continuent d'ouvrir pendant",
                unitLabel: "Unité de durée",
                weeks: "semaines",
                months: "mois",
                suffix: "après l'ouverture de la première évaluation",
                lastOpening: "La dernière évaluation s'ouvre le {{date}}. Un cycle ne peut pas s'étendre sur plus d'un an.",
                cap: "Un cycle ne peut pas s'étendre sur plus d'un an."
            }
        },
        close: {
            label: "Fermeture du cycle",
            hint: "Toutes les évaluations sont verrouillées et passent en lecture seule.",
            dateLabel: "Date de fermeture",
            floor: "Doit être le {{date}} ou plus tard, pour laisser à tout le monde le temps de terminer."
        },
        lateSubmissions: {
            label: "Soumission en retard",
            notAccepted: "Non acceptée",
            acceptedFor: "Acceptée pendant…",
            indefinitely: "Acceptée indéfiniment",
            daysLabel: "Jours après l'échéance de l'évaluation",
            daysSuffix_one: "jour après l'échéance de l'évaluation",
            daysSuffix_other: "jours après l'échéance de l'évaluation",
            help: {
                notAccepted: "Les évaluations se verrouillent une fois l'échéance passée.",
                acceptedFor: "Après ce délai, l'évaluation se verrouille et devient en lecture seule.",
                indefinitely: "Les évaluations ne se verrouillent jamais. Vous pouvez fermer et verrouiller le cycle vous-même."
            }
        },
        reviewWindows: {
            label: "Échéances",
            focalHint: "Les évaluations en retard peuvent toujours être soumises jusqu'à la fermeture du cycle."
        },
        recurrence: {
            label: "Récurrence",
            switchLabel: "Répéter ce cycle",
            nextCycle: "Le prochain cycle débute le {{startDate}}. Son brouillon est créé un mois à l'avance, le {{draftDate}}.",
            nextCycleFromLaunch: "Le prochain cycle débute une période d'évaluation plus tard, à partir du lancement de celui-ci. Son brouillon est créé un mois à l'avance.",
            nextCycleFromLastOpening: "Le prochain cycle débute le lendemain de l'ouverture de la dernière évaluation. Son brouillon est créé un mois à l'avance."
        }
    },
    CycleReviewWindows: {
        reviewTypes: {
            self: "Auto-évaluation",
            manager: "Évaluation par le gestionnaire",
            peer: "Évaluation par les pairs",
            upward: "Évaluation ascendante"
        },
        table: {
            caption: "Échéances par type d'évaluation",
            reviewType: "Type d'évaluation",
            deadline: "Date limite de soumission",
            dueAfter: "Échéance après le début de la rédaction",
            days: "jours",
            deadlineLabel: "{{reviewType}} : date limite de soumission",
            dueAfterLabel: "{{reviewType}} : échéance après le début de la rédaction, en jours"
        },
        timeline: {
            sliderLabel: "{{reviewType}} : échéance",
            dueOn: "Échéance le {{date}}",
            dueOnDay: "Échéance au jour {{day}}",
            noDueDate: "Aucune échéance",
            launch: "Lancement",
            day: "Jour {{day}}",
            overflow: {
                self: "Les auto-évaluations se verrouillent après la fermeture du cycle.",
                manager: "Les évaluations par le gestionnaire se verrouillent après la fermeture du cycle.",
                peer: "Les évaluations par les pairs se verrouillent après la fermeture du cycle.",
                upward: "Les évaluations ascendantes se verrouillent après la fermeture du cycle."
            }
        }
    },
    CycleNotificationsSummary: {
        subheading: "Voici un aperçu de toutes les notifications automatisées qui seront envoyées pendant le cycle d'évaluation de performance. Ces notifications permettent de tenir les participants informés et d'assurer que les évaluations sont complétées dans les délais.",
        filterLabel: "Filtrer par destinataire",
        tableLabel: "Notifications automatisées",
        columns: {
            notification: "Notification",
            goesTo: "Destinataires",
            sent: "Envoi"
        },
        audiences: {
            all: "Tous",
            reviewer: "Évaluateur",
            directManager: "Gestionnaire immédiat",
            reviewee: "Personne évaluée",
            peerReviewer: "Pair",
            upwardReviewer: "Évaluateur ascendant"
        },
        notifications: {
            reviewCycleKickoff: {
                title: "Lancement du cycle d'évaluation",
                description: "Informe les participants que le cycle d'évaluation a été lancé et fournit un aperçu du processus.",
                trigger: "Envoyé au début du cycle"
            },
            peerReviewersSelection: {
                title: "Sélection des pairs",
                description: "Informe les gestionnaires de sélectionner les pairs pour leurs subordonnés directs.",
                trigger: "Envoyé au début du cycle"
            },
            peerReviewRequest: {
                title: "Demande d'évaluation par les pairs",
                description: "Informe les participants de compléter leurs évaluations par les pairs.",
                trigger: "Envoyé lorsque les pairs sont sélectionnés"
            },
            upwardReviewersSelection: {
                title: "Sélection des évaluateurs ascendants",
                description: "Informe les gestionnaires de sélectionner les évaluateurs ascendants pour leurs subordonnés directs.",
                trigger: "Envoyé au début du cycle"
            },
            upwardReviewRequest: {
                title: "Demande d'évaluation ascendante",
                description: "Informe les participants de compléter leurs évaluations ascendantes.",
                trigger: "Envoyé lorsque les évaluateurs ascendants sont sélectionnés"
            },
            temporaryManagerAssignation: {
                title: "Attribution de gestionnaire immédiat temporaire",
                description: "Informe les participants qu'ils ont été assignés comme gestionnaire immédiat temporaire pour les subordonnés directs d'autres gestionnaires.",
                trigger: "Envoyé lors de l'assignation comme gestionnaire immédiat temporaire"
            },
            reviewReminder: {
                title: "Rappel d'évaluation",
                description: "Rappelle aux participants de compléter leurs évaluations avant la date limite.",
                trigger: "Envoyé 3 jours ouvrables avant la date limite de soumission"
            },
            reviewDueToday: {
                title: "Évaluation à remettre aujourd'hui",
                description: "Rappelle aux participants que leur évaluation est à remettre aujourd'hui.",
                trigger: "Envoyé à la date limite de soumission"
            },
            reviewOverdue: {
                title: "Évaluation en retard",
                description: "Informe les participants que leur évaluation est en retard.",
                trigger: "Envoyé après la date limite de soumission"
            },
            submitConfirmation: {
                title: "Confirmation de soumission d'évaluation",
                description: "Confirme aux participants que leurs évaluations ont été soumises avec succès.",
                trigger: "Envoyé lorsque l'évaluation est soumise"
            },
            managerDailyDigest: {
                title: "Sommaire quotidien du gestionnaire",
                description: "Fournit aux gestionnaires un résumé quotidien de l'activité de leurs subordonnés directs : évaluations complétées, évaluations en retard, et subordonnés directs récemment ajoutés ou retirés.",
                trigger: "Envoyé quotidiennement lorsqu'il y a de l'activité à signaler"
            },
            reviewShared: {
                title: "Évaluation partagée",
                description: "Informe les personnes évaluées que l'évaluation par le gestionnaire, par les pairs et/ou ascendante a été partagée avec elles.",
                trigger: "Envoyé lorsque les évaluations sont partagées"
            },
            newlyAddedParticipant: {
                title: "Nouveaux participants ajoutés",
                description: "Informe les participants qu'ils ont été ajoutés à un cycle en cours après son lancement et que des tâches leur sont attribuées.",
                trigger: "Envoyé lorsqu'ils sont ajoutés à un cycle en cours"
            }
        }
    },
    ReviewScoreField: {
        scaleLabel: "Échelle du score d'évaluation",
        scaleOption: "Sur {{scale}}",
        modeLabel: "Formule",
        mode: {
            "average-manager-questions": {
                label: "Moyenne des réponses de cote du gestionnaire",
                description: "La moyenne de toutes les réponses de cote données par le gestionnaire."
            },
            "average-all-questions": {
                label: "Moyenne des réponses de cote de tous les évaluateurs",
                description: "La moyenne de toutes les réponses de cote, tous évaluateurs confondus."
            },
            custom: {
                label: "Formule personnalisée",
                description: "Choisissez quelles cotes comptent, et dans quelle proportion."
            }
        }
    },
    NineBoxField: {
        inclusionLabel: "Inclure une grille 9 cases",
        performanceLabel: "Performance",
        performanceMode: {
            "overall-score": {
                label: "Score d'évaluation",
                description: "Utiliser le score que ce cycle calcule déjà."
            },
            custom: {
                label: "Personnalisé",
                description: "Choisissez des questions de cote précises et attribuez un poids à chacune."
            }
        },
        potentialLabel: "Potentiel",
        potentialMode: {
            "single-question": {
                label: "Une seule question",
                description: "Calculer cet axe à partir d'une seule question de cote."
            },
            custom: {
                label: "Personnalisé",
                description: "Choisissez des questions de cote précises et attribuez un poids à chacune."
            }
        },
        questionLabel: "Question",
        questionPlaceholder: "Sélectionner une question",
        reviewTypeLabel: "Type d'évaluation",
        reviewTypePlaceholder: "Sélectionner",
        scaleLabel: "Échelle de rapport",
        reviewType: {
            manager: "Gestionnaire",
            self: "Auto-évaluation",
            peer: "Pair",
            upward: "Ascendante"
        }
    },
    NoRatingQuestionsHint: {
        message: "Aucune question de cote : ce cycle ne produira pas de score.",
        addRatingQuestion: "Ajouter une question de cote"
    },
    ScoreFormulaQuestionSelect: {
        untitledQuestion: "Question sans titre",
        addRatingQuestion: "Ajouter une question de cote"
    },
    ScoreFormulaComponentsField: {
        questionColumn: "Question",
        reviewTypeColumn: "Type d'évaluation",
        weightColumn: "Poids",
        questionPlaceholder: "Sélectionner une question",
        reviewTypePlaceholder: "Sélectionner",
        weightPlaceholder: "Égal",
        addComponent: "Ajouter une entrée",
        removeComponent: "Retirer la question",
        weightSum: "Total : {{percent}} %",
        weightSumRequirement: "Les poids doivent totaliser 100 %.",
        weightAuto: "Auto",
        reviewType: {
            manager: "Gestionnaire",
            self: "Auto-évaluation",
            peer: "Pair",
            upward: "Ascendante"
        }
    },
    CycleReviewTypesField: {
        legend: "Types d'évaluation",
        lastReviewType: "{{description}} Un cycle doit compter au moins un type d'évaluation, celui-ci ne peut donc pas être désactivé.",
        reviewTypes: {
            self: {
                label: "Auto-évaluation",
                description: "La personne évaluée apprécie son propre travail."
            },
            manager: {
                label: "Évaluation par le gestionnaire",
                description: "Son gestionnaire direct l'évalue."
            },
            peer: {
                label: "Évaluation par les pairs",
                description: "Les collègues avec qui elle travaille l'évaluent."
            },
            upward: {
                label: "Évaluation ascendante",
                description: "Les personnes qui lui sont subordonnées l'évaluent."
            }
        },
        peer: {
            count: {
                label: "Nombre suggéré d'évaluateurs pairs",
                description: "Les gestionnaires les désignent à l'ouverture du cycle."
            }
        },
        upward: {
            nomination: {
                label: "Comment les évaluateurs sont choisis",
                "direct-reports": {
                    label: "Tous les subordonnés directs"
                },
                "reporting-line": {
                    label: "Toute la ligne hiérarchique",
                    description: "Toutes les personnes sous elle, à tout niveau."
                },
                manual: {
                    label: "Manuel",
                    description: "Un administrateur ou un gestionnaire les choisit."
                }
            }
        },
        attribution: {
            label: "Noms des auteurs",
            anonymous: {
                label: "Masqués aux personnes évaluées",
                description: "Les gestionnaires et les cadres au-dessus d'elles voient tout de même qui a écrit quoi."
            },
            identified: {
                label: "Visibles aux personnes évaluées"
            }
        }
    },
    QuestionnaireBuilder: {
        addQuestion: "Question",
        addSection: "Section",
        outlineLabel: "Contenu du questionnaire",
        limitReached: "Vous avez atteint la limite de {{count}} sections et questions.",
        empty: "Ajoutez une question pour commencer à créer le formulaire d'évaluation.",
        untitledQuestion: "Question sans titre",
        untitledSection: "Section sans titre",
        reorderSection: "Réorganiser la section",
        reorderQuestion: "Réorganiser la question",
        sectionPosition: "{{name}}, section {{position}} sur {{total}}",
        questionPosition: "{{name}}, question {{position}} sur {{total}} dans {{section}}",
        questionNumber: "Question {{number}}",
        sectionTitlePlaceholder: "Ajouter un nom de section",
        sectionDescriptionPlaceholder: "Expliquez sur quoi porte cette section (facultatif)",
        questionTitlePlaceholder: "Que voulez-vous demander?",
        questionDescriptionPlaceholder: "Description (facultatif)",
        duplicateQuestion: "Dupliquer la question",
        deleteQuestion: "Supprimer la question",
        deleteSection: "Supprimer la section",
        kinds: {
            text: "Texte libre",
            rating: "Échelle d'évaluation",
            choice: "Choix multiple"
        },
        answerLabel: "Réponse",
        kindLabel: "Type de réponse",
        textPreview: "Réponse en texte libre",
        addChoice: "Ajouter un choix",
        choicePlaceholder: "Choix {{number}}",
        deleteChoice: "Supprimer le choix",
        allowMultiple: "Autoriser la personne à choisir plus d'une réponse",
        addRatingPoint: "Ajouter un point",
        ratingPointPlaceholder: "Libellé du point {{number}}",
        ratingPointDescriptionLabel: "Description du point {{number}}",
        ratingPointDescriptionPlaceholder: "Ce que ce point signifie (facultatif)",
        deleteRatingPoint: "Supprimer l'option",
        audiences: {
            self: "Personne évaluée",
            manager: "Gestionnaire",
            peer: "Pairs",
            upward: "Personnes supervisées"
        },
        responses: {
            self: "Ses propres réponses",
            manager: "Les réponses du gestionnaire",
            peer: "Les réponses des pairs",
            upward: "Les réponses des personnes qu'elle supervise"
        },
        whoAnswers: "Qui répond à cette question",
        noReviewTypes: "Ce cycle ne comporte aucune évaluation pour l'instant.",
        required: "Obligatoire",
        sharedWithReviewee: "Partagé avec la personne évaluée",
        nobodyAsked: "Personne n'a encore cette question à remplir.",
        rewordFor: "Reformuler pour",
        rewordingFor: "Formulation pour {{audience}}",
        useSharedWording: "Utiliser la formulation commune pour {{audience}}",
        issues: {
            noQuestions: "Au moins une section et une question sont obligatoires.",
            emptySection: "Cette section est vide.",
            untitledSection: "Cette section doit avoir un nom.",
            noAudience: "Attribuez cette question à au moins une évaluation.",
            untitledQuestion: "Cette question doit avoir une formulation.",
            tooFewChoices: "Offrez au moins {{count}} choix.",
            unlabelledChoice: "Un choix n'a pas de libellé.",
            tooFewRatingPoints: "Offrez au moins {{count}} options d'échelle.",
            unlabelledRatingPoint: "Un point de l'échelle n'a pas de libellé.",
            tooManySlots: "Vous avez atteint la limite de {{count}} sections et questions.",
            reviewTypeWithoutRequiredQuestion: "{{audience}} doit avoir au moins une question obligatoire."
        }
    },
    CycleBuilderFooter: {
        cancel: "Annuler",
        saving: "Enregistrement en cours. Patientez un instant.",
        saved: "Toutes les modifications ont été enregistrées",
        saveError: "Modifications non enregistrées",
        saveRefused: "Certaines modifications n'ont pas pu être enregistrées"
    },
    FeaturePage: {
        workleapGuardrails: {
            title: "Garde-fous Workleap",
            description: "Intégrés. Toujours actifs.",
            showAll: "Afficher les {{count}}",
            showFewer: "Afficher moins",
            rules: {
                engagement: [
                    "Ne jamais attribuer une citation ou une réponse à une personne précise lorsque l'anonymat a été promis.",
                    "Lorsque l'anonymat est compromis par un échantillon trop petit, supprimer le thème plutôt que de nommer les personnes.",
                    "Ne jamais affirmer une cause d'un changement de score que les données ne peuvent pas étayer.",
                    "Supprimer tout score de sous-groupe qui passe sous le seuil d'anonymat.",
                    "Ne jamais attribuer un commentaire à une personne lorsque le feedback a été soumis de façon anonyme.",
                    "Ne pas laisser un grand thème éclipser un signal plus petit mais réel.",
                    "Ne jamais promettre un engagement que le gestionnaire n'a pas approuvé.",
                    "Préserver l'anonymat de la source lorsque le feedback a été soumis de façon anonyme.",
                    "Agréger les signaux. Ne jamais inclure de réponses individuelles ni quoi que ce soit qui pourrait désanonymiser une petite équipe.",
                    "Supprimer toute métrique qui passe sous le seuil d'anonymat plutôt que de la rapporter."
                ],
                performance_reviews: [
                    "Rien n'est inventé. Chaque affirmation renvoie à quelque chose de réel : une mise à jour d'objectif, une note ou un feedback ; et quand les preuves manquent, il le dit et n'écrit presque rien plutôt que d'inventer une ébauche convaincante.",
                    "Il décrit, il ne décide pas. Il nomme des tendances et expose les faits, mais ne rend jamais de verdict et ne recommande aucune décision. Ce choix vous revient.",
                    "Il ne voit que ce que vous avez le droit de voir. L'accès suit les permissions de chaque type d'évaluation ; un pair évaluateur ne peut donc pas consulter l'historique privé des 1:1, par exemple.",
                    "Il fait ressortir ce qui est difficile, pas seulement ce qui est facile. Les engagements manqués ou l'engagement en baisse sont nommés clairement plutôt qu'atténués.",
                    "La cote reste la vôtre. Il peut exposer ce que les preuves soutiennent, mais ne propose jamais de cote, même sous pression.",
                    "Vos mots priment. Il n'écrasera pas vos modifications et n'imposera pas une « meilleure » version que ce que vous avez écrit.",
                    "Rien ne part sans vous. Il rédige ; vous modifiez et approuvez. Il ne soumet jamais en votre nom et n'agit pas sans y être invité."
                ],
                people: [
                    "Rien n'est inventé. Chaque affirmation sur une personne provient du contexte accessible pour la demande.",
                    "Il n'évalue ni la performance ni le potentiel, ne prédit pas le départ d'une personne et ne tire aucune conclusion sur son caractère, sa santé ou sa situation personnelle.",
                    "Il indique quand le contexte manque plutôt que de présenter un portrait incomplet comme s'il était complet."
                ]
            }
        }
    }
};
